import { useLocale, type Locale } from '@ministry/ui/internal';

/**
 * App-owned screen copy (login, MFA, shell chrome). `@ministry/ui`'s own `useLocale().t`
 * is scoped to the design system's OWN component-chrome strings on purpose (see that
 * package's `locale-context.tsx`) — "consuming apps own their own screen copy and bring
 * their own i18n for it". This is that i18n, sharing the same `Locale` and the same
 * `<html lang>`-driving provider so the two never disagree about which language is active.
 *
 * A-020: every Nepali string here is a first-pass draft, not native-speaker reviewed.
 */
type Dict = Record<string, string>;

const en: Dict = {
  'login.title': 'Staff Login',
  'login.ministry.label': 'Ministry code',
  'login.ministry.hint': 'The 2-6 letter prefix on your tracking codes, e.g. MOHA.',
  'login.ministry.invalid': 'Enter the 2-6 letter ministry code.',
  'login.username.label': 'Username or email',
  'login.password.label': 'Password',
  'login.showPassword': 'Show password',
  'login.hidePassword': 'Hide password',
  'login.submit': 'Log in',
  'login.submitting': 'Logging in',
  'login.forgotPassword': 'Forgot password?',
  'login.required': 'This field is required.',
  'login.error.invalidCredentials': 'Incorrect username or password.',
  'login.error.locked': 'This account is temporarily locked. Try again in {minutes} minute(s), or contact Admin/HR.',
  'login.error.rateLimited': 'Too many attempts. Please wait a moment and try again.',
  'login.error.network': 'Something went wrong on our side. Please check your connection and try again.',
  'login.error.generic': 'Something went wrong. Please try again.',
  'common.retry': 'Retry',
  'mfa.title': 'Verification code',
  'mfa.instructions': 'Enter the 6-digit code from your authenticator app.',
  'mfa.codeLabel': '6-digit code',
  'mfa.submit': 'Verify',
  'mfa.submitting': 'Verifying',
  'mfa.error.invalid': 'Incorrect code. Try again.',
  'mfa.error.rateLimited': 'Too many attempts. Please wait a moment and try again.',
  'mfa.error.network': 'Something went wrong on our side. Please try again.',
  'mfa.error.generic': 'Something went wrong. Please try again.',
  'mfa.contactAdmin': 'Lost your device? Contact Admin/HR — this cannot be reset by yourself.',
  'mfa.backToLogin': 'Back to login',

  // ---- MFA enrolment (R-033) --------------------------------------------------------
  'mfaEnrol.title': 'Set up your authenticator',
  'mfaEnrol.noToken.title': 'This link is missing its enrolment token',
  'mfaEnrol.noToken.body': 'Open the link an administrator gave you again, or ask them for a new one.',
  'mfaEnrol.loading': 'Loading your enrolment details',
  'mfaEnrol.tokenInvalid.title': 'This enrolment link is no longer valid',
  'mfaEnrol.tokenInvalid.body': 'It may have expired, already been used, or been mistyped. Ask an administrator for a new enrolment link — this cannot be fixed by retrying.',
  'mfaEnrol.error.network': 'Something went wrong on our side. Please check your connection and try again.',
  'mfaEnrol.error.generic': 'Something went wrong. Please try again.',
  'mfaEnrol.instructions': 'Scan this code with your authenticator app (e.g. Google Authenticator, Authy) for {username}, then enter the 6-digit code it shows below.',
  'mfaEnrol.qrLabel': 'Scan with your authenticator app to set up two-factor sign-in',
  'mfaEnrol.secretLabel': "Can't scan? Enter this key manually instead:",
  'mfaEnrol.secretCopy': 'Copy',
  'mfaEnrol.secretCopied': 'Copied',
  'mfaEnrol.codeLabel': '6-digit code',
  'mfaEnrol.confirmSubmit': 'Activate',
  'mfaEnrol.confirming': 'Activating',
  'mfaEnrol.confirm.error.invalid': 'Incorrect code. Check your authenticator app and try again.',
  'mfaEnrol.confirmed.title': 'Your authenticator is set up',
  'mfaEnrol.confirmed.body': 'You can now log in with your username, password, and a code from your authenticator app.',
  'mfaEnrol.confirmed.goToLogin': 'Go to login',
  'shell.dashboard': 'Dashboard',
  'shell.nav.label': 'Main navigation',
  'shell.nav.administration': 'Administration',
  'common.notSet': 'Not set',
  'shell.nav.openMenu': 'Open navigation menu',
  'shell.nav.files': 'File Register',
  'shell.nav.fileNew': 'File Intake',
  'shell.nav.tasks': 'My Tasks',
  'shell.nav.account': 'Account Settings',
  'shell.systemName': 'Ministry File Tracking, Workflow & Office Visit Management Portal',
  'shell.logout': 'Log out',
  'shell.loggingOut': 'Logging out',
  'shell.theme.toggle': 'Toggle color theme',
  'shell.theme.light': 'Light',
  'shell.theme.dark': 'Dark',
  'shell.theme.system': 'System',
  'shell.theme.switchToLight': 'Switch to light theme',
  'shell.theme.switchToDark': 'Switch to dark theme',
  'shell.locale.toggle': 'Switch language',
  'shell.locale.nepali': 'Nepali',
  'shell.locale.english': 'English',
  'shell.locale.switchTo': 'Switch language from {current} to {next}.',
  'shell.notifications': 'Notifications (not available in this build)',
  'shell.skipToContent': 'Skip to main content',
  'shell.sessionLoading': 'Checking your session',
  'shell.mustChangePassword': 'Your account requires a password change before continuing.',
  'shell.mustChangePassword.action': 'Change password now',
  'shell.account.menuLabel': 'Account menu for {name}',
  'shell.account.roleSummary': '{name}, {role}',
  'shell.account.settings': 'Account settings',
  'shell.account.logout': 'Log out',
  'accountSettings.title': 'Account Settings',
  'accountSettings.identity.title': 'Signed in as',
  'accountSettings.language.title': 'Language',
  'accountSettings.language.body': 'Applies immediately, and is the same control as the top bar’s language switch.',
  'accountSettings.theme.title': 'Color theme',
  'accountSettings.theme.body': 'Applies immediately, and is the same control as the top bar’s theme switch.',
  'accountSettings.password.title': 'Change password',
  'accountSettings.password.body': 'Choose a new password. You will need your current one.',
  'accountSettings.password.current': 'Current password',
  'accountSettings.password.new': 'New password',
  'accountSettings.password.confirm': 'Confirm new password',
  'accountSettings.password.submit': 'Change password',
  'accountSettings.password.submitting': 'Changing password…',
  'accountSettings.password.success': 'Your password has been changed.',
  'accountSettings.password.forcedNotice': 'Your account requires a password change before continuing.',
  'accountSettings.password.error.wrongCurrent': 'Your current password is incorrect.',
  'accountSettings.password.error.network': 'Something went wrong on our side. Please try again.',
  'accountSettings.password.error.generic': 'Your password could not be changed. Please try again.',
  'accountSettings.password.notAvailable.title': 'Not available on this server yet',
  'accountSettings.password.notAvailable.body':
    'Self-service password change is specified but not deployed on this server yet. Contact Admin/HR to have your password reset.',
  'accountSettings.password.rule.length': '{min}–{max} characters',
  'accountSettings.password.rule.notCommon': "Doesn't contain a common word (e.g. \"password\", \"ministry\")",
  'accountSettings.password.rule.notUsername': "Doesn't contain your username",
  'accountSettings.password.rule.mismatch': 'Passwords do not match.',
  'accountSettings.password.rule.serverRejected.too_short': 'Too short.',
  'accountSettings.password.rule.serverRejected.too_long': 'Too long.',
  'accountSettings.password.rule.serverRejected.blocked_term': 'Contains a common blocked word.',
  'accountSettings.password.rule.serverRejected.contains_username': 'Contains your username.',
  'accountSettings.notBuilt.title': 'Not built in this round',
  'accountSettings.notBuilt.body':
    'MFA enrollment and notification-channel preferences are specified in SCREEN_SPECS.md I23 but are not wired to a backend endpoint in this build.',
  'dashboard.title': 'Home',
  'dashboard.welcome': 'Welcome, {name}',
  'dashboard.welcomeWithRole': 'Welcome, {name} · {role}',
  'dashboard.body': 'You are signed in. The rest of this workspace is not built in this round.',
  'dashboard.roles': 'Roles: {roles}',
  'dashboard.myFiles.title': 'Files in your scope',
  'dashboard.overdue.title': 'Overdue',
  'dashboard.dueSoon.title': 'Due within 3 days',
  'dashboard.pendingAppointments.title': 'Appointments awaiting your decision',
  'dashboard.stageChart.title': 'Open files by stage',
  'dashboard.stageChart.statusColumn': 'Status',
  'dashboard.stageChart.countColumn': 'Files',
  'dashboard.tasksTitle': 'My Tasks',
  'dashboard.tasksViewAll': 'View all tasks',
  'dashboard.tasksEmpty.title': 'No files assigned right now',
  'dashboard.tasksEmpty.body': "You're all caught up.",
  'dashboard.noFileAccess.title': 'Nothing to show yet',
  'dashboard.noFileAccess.body': 'Your account does not have file-register access in this build.',
  'dashboard.tasksError': 'Could not load your task queue.',
  'common.you': 'You',
  'common.unassigned': 'Unassigned',
  'common.notAvailable': '—',
  'common.clearFilters': 'Clear filters',
  'common.viewAll': 'View all',
  'common.somethingWentWrong': 'Something went wrong. Please try again.',
  'fileRegister.title': 'File Register',
  'fileRegister.resultCount': '{count} file(s) in your scope',
  'fileRegister.searchLabel': 'Search by subject or tracking code',
  'fileRegister.searchPlaceholder': 'e.g. MW-2083-000001',
  'fileRegister.filterStatus': 'Status',
  'fileRegister.filterStatusAll': 'All statuses',
  'fileRegister.filterConfidentiality': 'Confidentiality',
  'fileRegister.filterConfidentialityAll': 'All levels',
  'fileRegister.filterOverdueOnly': 'Overdue only',
  'fileRegister.clearFilters': 'Clear filters',
  'fileRegister.caption': 'Files in your scope',
  'fileRegister.column.trackingCode': 'Tracking Code',
  'fileRegister.column.status': 'Status',
  'fileRegister.column.overdue': 'Overdue',
  'fileRegister.column.subject': 'Subject',
  'fileRegister.column.dueDate': 'Deadline',
  'fileRegister.column.assignedTo': 'Assigned To',
  'fileRegister.assignedToOther': 'Assigned (name not shown here)',
  'fileRegister.column.section': 'Section',
  'fileRegister.column.registeredDate': 'Registered (BS)',
  'fileRegister.column.confidentiality': 'Confidentiality',
  'fileRegister.subjectRedacted': 'Redacted — above your clearance',
  'fileRegister.noDueDate': 'No deadline set',
  'fileRegister.emptyScope.title': 'No files in your scope yet',
  'fileRegister.emptyScope.body': 'Files registered to your section will appear here.',
  'fileRegister.emptyFiltered.title': 'No files match your filters',
  'fileRegister.emptyFiltered.body': 'Try widening your search or clearing a filter.',
  'fileRegister.forbidden.title': "You can't view the file register",
  'fileRegister.forbidden.role.body':
    'Your role does not include file-register access. Contact your section administrator if you believe this is wrong.',
  'fileRegister.forbidden.scope.body': 'This register is outside the organisational scope your account is assigned to.',
  'fileRegister.bulkAction.selectedCount': '{count} file(s) selected',
  'fileRegister.bulkAction.reassign': 'Reassign selected',
  'fileRegister.bulkAction.clearSelection': 'Clear selection',
  'fileRegister.bulkReassign.title': 'Reassign selected files',
  'fileRegister.bulkReassign.body': 'Reassigning {count} file(s). For when the current holder cannot act on them.',
  'fileRegister.bulkReassign.submit': 'Reassign files',
  'fileRegister.bulkReassign.targetHelper': 'The user ID to reassign these files to (no picker is available for bulk reassignment in this build — see the handback).',
  'fileRegister.bulkReassign.someUnavailable': "Couldn't load the current version for these files — they will be skipped:",
  'fileRegister.bulkReassign.resultSummary': '{succeeded} succeeded, {failed} failed',
  'fileRegister.bulkReassign.itemOk': 'Reassigned',
  'fileRegister.bulkReassign.itemFailed': 'Failed',
  'fileDetail.back': 'Back to File Register',
  'fileDetail.loading': 'Loading file…',
  'fileDetail.tabOverview': 'Overview',
  'fileDetail.tabHistory': 'History',
  'fileDetail.tabAttachments': 'Attachments',
  'fileDetail.tabDarta': 'Darta-Chalani',
  'fileDetail.registeredOn': 'Registered {date}',
  'fileDetail.section': 'Section: {name}',
  'fileDetail.dueOn': 'Due {date}',
  'fileDetail.noDueDate': 'No deadline set',
  'fileDetail.applicantTitle': 'Applicant',
  'fileDetail.applicantPhone': 'Phone',
  'fileDetail.applicantEmail': 'Email',
  'fileDetail.applicantAddress': 'Address',
  'fileDetail.applicantNone': 'No applicant on record.',
  'fileDetail.notProvided': 'Not provided',
  'fileDetail.dartaNumber': 'Darta number',
  'fileDetail.chalaniNumber': 'Chalani number',
  'fileDetail.dartaRegisterDate': 'Register date',
  'fileDetail.dartaNotLinked': 'No darta-chalani reference linked to this file yet.',
  'fileDetail.attachmentsEmpty.title': 'No attachments',
  'fileDetail.attachmentsEmpty.body': 'No documents have been attached to this file yet.',
  'fileDetail.attachmentsError.title': 'Could not load attachments',
  'fileDetail.attachmentPendingScan': 'Scan pending — not yet available',
  'fileDetail.attachmentAboveInternal': 'Above Internal — requires an authorized download',
  'fileDetail.attachmentDownload': 'Download',
  'fileDetail.preview.action': 'Preview',
  'fileDetail.preview.close': 'Close',
  'fileDetail.preview.downloadHint': 'Previewed in your browser — download it for anything else.',
  'fileDetail.preview.pdfTitle': 'Preview of {name}',
  'fileDetail.preview.imageAlt': 'Preview of {name}',
  'fileDetail.preview.unsupported.title': 'No preview available for this file type',
  'fileDetail.preview.unsupported.body': 'Only PDF and image attachments can be previewed here. Use Download to open this file.',
  'fileDetail.preview.error.notFound': "This attachment couldn't be found.",
  'fileDetail.preview.error.forbidden': "You don't have permission to view this attachment.",
  'fileDetail.preview.error.network': 'Something went wrong on our side. Please try again.',
  'fileDetail.preview.error.generic': 'This attachment could not be previewed.',
  'fileDetail.actionsTitle': 'Actions',
  'fileDetail.actionForward': 'Forward',
  'fileDetail.actionApprove': 'Approve',
  'fileDetail.actionReject': 'Reject',
  'fileDetail.actionComment': 'Comment',
  'fileDetail.notHolder':
    'This file is awaiting action from its current holder. You can add a comment, but you cannot forward, approve, or reject it.',
  'fileDetail.auditNotice': 'This access has been recorded, because this file is above Internal confidentiality.',
  'fileDetail.error.notFound.title': "This file couldn't be found",
  'fileDetail.error.notFound.body': 'It may have been moved, or the link may be incorrect.',
  'fileDetail.error.forbiddenRole.title': "You don't have permission to view this",
  'fileDetail.error.forbiddenRole.body':
    'Your role does not include file access. Contact your section administrator if you believe this is wrong.',
  'fileDetail.error.forbiddenScope.title': 'Outside your scope',
  'fileDetail.error.forbiddenScope.body': 'This file belongs to an organisational unit outside your assigned scope.',
  'fileDetail.error.forbiddenOwnership.title': "You're not this file's current holder",
  'fileDetail.error.forbiddenOwnership.body':
    "This file exists and is in your scope, but only its current holder can view or act on it this way.",
  'fileDetail.error.forbiddenClearance.title': 'Clearance required',
  'fileDetail.error.forbiddenClearance.body': "This file's confidentiality level is above your clearance.",
  'fileDetail.error.generic.title': "We couldn't load this file",
  'fileDetail.error.generic.body': 'Something went wrong on our side.',
  'fileDetail.movementsError.title': 'Could not load the movement history',
  'movement.title.forward': 'Forward this file',
  'movement.title.approve': 'Approve this file',
  'movement.title.reject': 'Reject this file',
  'movement.title.comment': 'Add a comment',
  'movement.description.forward': 'Choose who this file goes to next. Only valid next holders are listed.',
  'movement.description.approve': 'This file will be marked Approved. It can still be forwarded or completed afterward.',
  'movement.description.reject':
    'This file will be marked Rejected and the applicant will be notified. This cannot be undone from this screen.',
  'movement.description.comment': "Add a note to this file's history. This does not change its status.",
  'movement.forwardTarget.label': 'Forward to',
  'movement.forwardTarget.placeholder': 'Search by name or section',
  'movement.forwardTarget.helper':
    "This is the server's own list of valid next holders — officers on leave or without a live placement are excluded.",
  'movement.forwardTarget.empty': 'No valid next holders are available for this file right now.',
  'movement.forwardTarget.onLeave': ' (on leave)',
  'movement.remarks.label': 'Remarks',
  'movement.remarks.requiredHelper': 'Required — a rejection with no stated reason cannot be recorded.',
  'movement.remarks.optionalHelper': 'Optional, but recommended for the next holder.',
  'movement.remarks.required': 'Remarks are required to reject a file.',
  'movement.submit': 'Submit',
  'movement.submitting': 'Submitting',
  'movement.cancel': 'Cancel',
  'movement.error.conflict.title': 'This file has changed since you opened it',
  'movement.error.conflict.body':
    'Current status: {status}. Current holder: {holder}. Review the file again before retrying — nothing was submitted.',
  'movement.error.conflict.holderOther': 'someone else',
  'movement.error.staleReload': 'Reload this file',
  'movement.error.ownership.title': 'You can no longer act on this file',
  'movement.error.ownership.body': 'Its holder changed before this was submitted. Nothing was recorded.',
  'movement.error.state.title': 'This action is not possible right now',
  'movement.error.state.body': "This file's current status does not allow this action. Nothing was recorded.",
  'movement.error.target.title': 'That officer is no longer a valid target',
  'movement.error.target.body': 'Choose another officer from the list — nothing was recorded.',
  'movement.error.network': 'Something went wrong on our side. Nothing was recorded — try again.',
  'movement.error.generic': "This action could not be completed. The file's status has not changed.",
  'movement.success': "Recorded — the file's status and history now reflect this action.",
  'myTasks.title': 'My Tasks',
  'myTasks.statTotal': 'Total assigned',
  'myTasks.statOverdue': 'Overdue',
  'myTasks.filterOverdueOnly': 'Overdue only',
  'myTasks.caption': 'Files assigned to you',
  'myTasks.column.trackingCode': 'Tracking Code',
  'myTasks.column.subject': 'Subject',
  'myTasks.column.status': 'Status',
  'myTasks.column.dueDate': 'Deadline',
  'myTasks.empty.title': 'You have no files assigned right now',
  'myTasks.empty.body': 'When a file is forwarded to you, it will appear here.',
  'myTasks.noDueDate': '—',
  'notFound.title': 'Page not found',
  'notFound.body': "The page you're looking for doesn't exist or you don't have a menu item pointing at it yet.",
  'notFound.action': 'Back to dashboard',
  'errorBoundary.title': 'Something went wrong',
  'errorBoundary.body': 'This screen ran into an unexpected error. Reloading usually fixes it.',
  'errorBoundary.reload': 'Reload page',

  // ---- I11 Notification Centre ------------------------------------------------------
  'notifications.markAllRead': 'Mark all read',
  'notifications.markingAll': 'Marking…',
  'notifications.title': 'Notifications',
  'notifications.deepLinkHint': 'Opens the related file',

  // ---- I7 File Intake -----------------------------------------------------------------
  'fileIntake.title': 'Register a New File',
  'fileIntake.subtitle': 'Generates the tracking code and QR receipt in one transaction — nothing is created until you confirm on the review step.',
  'fileIntake.step.applicant': 'Applicant details',
  'fileIntake.step.file': 'File details',
  'fileIntake.step.review': 'Review',
  'fileIntake.applicant.fullName': "Applicant's full name",
  'fileIntake.applicant.phone': 'Phone number',
  'fileIntake.applicant.phone.helper': "The citizen will use the last 4 digits of this number to check their file's status online.",
  'fileIntake.applicant.phone.error': 'Enter a valid Nepal mobile number (e.g. 98XXXXXXXX or +97798XXXXXXXX).',
  'fileIntake.applicant.email': 'Email (optional)',
  'fileIntake.applicant.email.error': 'Enter a valid email address.',
  'fileIntake.applicant.address': 'Address (optional)',
  'fileIntake.applicant.required': 'This field is required.',
  'fileIntake.file.subject': 'Subject',
  'fileIntake.file.subject.helper': 'What this file is about — at least 3 characters.',
  'fileIntake.file.subject.error': 'Subject must be between 3 and 500 characters.',
  'fileIntake.file.section': 'Section',
  'fileIntake.file.section.placeholder': 'Select the section this file belongs to',
  'fileIntake.file.section.helper': 'No `/reference/org-units` endpoint is available in this build — this list is built from sections seen on your own file register. If your section is not listed, at least one file must already exist in it, or ask Admin/HR.',
  'fileIntake.file.section.empty': 'No sections found. Open the File Register first, or contact Admin/HR — a file cannot be registered without a section.',
  'fileIntake.file.section.required': 'Select a section.',
  'fileIntake.darta.title': 'Darta-Chalani reference (optional)',
  'fileIntake.darta.helper': "Optional — can be linked later from the file's detail page if not available now.",
  'fileIntake.darta.dartaNumber': 'Darta number',
  'fileIntake.darta.chalaniNumber': 'Chalani number',
  'fileIntake.darta.registerDate': 'Register date',
  'fileIntake.nav.back': 'Back',
  'fileIntake.nav.next': 'Next',
  'fileIntake.nav.submit': 'Register file',
  'fileIntake.nav.submitting': 'Registering…',
  'fileIntake.review.title': 'Review before submitting',
  'fileIntake.review.body': 'Once submitted, a tracking code and QR receipt are generated and cannot be withdrawn from this screen. Check every field below.',
  'fileIntake.review.applicant': 'Applicant',
  'fileIntake.review.file': 'File',
  'fileIntake.review.darta': 'Darta-Chalani',
  'fileIntake.review.dartaNone': 'Not provided — can be linked later.',
  'fileIntake.error.title': 'The file could not be registered',
  'fileIntake.error.body': 'Nothing was created. Review the highlighted fields, or try again.',
  'fileIntake.error.network': 'Something went wrong on our side. Nothing was created — try again.',
  'fileIntake.error.sectionScope': 'That section is outside your assignment. Choose a section you are scoped to.',
  'fileIntake.success.title': 'File registered',
  'fileIntake.success.body': 'Tracking code {code} was generated. Give the citizen this code and QR — they will need it to check status online.',
  'fileIntake.success.trackingCode': 'Tracking code',
  'fileIntake.success.qrToken': 'QR token',
  'fileIntake.success.qrLabel': 'QR code for tracking code {code} — scan to open the public status page',
  'fileIntake.success.qrError': 'Could not load the QR code.',
  'fileIntake.success.print': 'Print receipt',
  'fileIntake.success.viewFile': 'Open this file',
  'fileIntake.newFile': 'New file',
  'fileIntake.forbidden.title': "You can't register a file",
  'fileIntake.forbidden.body': 'Your role does not include file intake. Contact Admin/HR if you believe this is wrong.',

  // ---- File Detail — additional actions ------------------------------------------------
  'fileDetail.recordActionsTitle': 'Record',
  'fileDetail.edit.action': 'Edit',
  'fileDetail.edit.title': 'Edit file',
  'fileDetail.edit.subject': 'Subject',
  'fileDetail.edit.dueDate': 'Due date',
  'fileDetail.edit.submit': 'Save changes',
  'fileDetail.edit.submitting': 'Saving…',
  'fileDetail.edit.success': 'File updated.',
  'fileDetail.darta.edit': 'Edit reference',
  'fileDetail.darta.editTitle': 'Darta-Chalani reference',
  'fileDetail.darta.success': 'Darta-Chalani reference saved.',
  'fileDetail.classify.action': 'Change classification',
  'fileDetail.classify.title': 'Change confidentiality classification',
  'fileDetail.classify.level': 'New confidentiality level',
  'fileDetail.classify.reason': 'Reason for this change',
  'fileDetail.classify.reason.required': 'A reason is required for a classification change.',
  'fileDetail.classify.submit': 'Change classification',
  'fileDetail.classify.success': 'Classification updated.',
  'fileDetail.qr.action': 'View QR / receipt',
  'fileDetail.qr.title': 'Citizen receipt',
  'fileDetail.qr.body': 'Give the citizen this tracking code and QR token — this is what they use on the public tracking page.',
  'fileDetail.qr.trackingCode': 'Tracking code',
  'fileDetail.qr.token': 'QR token',
  'fileDetail.qr.fragmentPath': 'Fragment path (never sent to a server)',
  'fileDetail.qr.error': 'Could not load the QR receipt.',
  'fileDetail.reopen.action': 'Re-open',
  'fileDetail.reopen.title': 'Re-open this file',
  'fileDetail.reopen.body': 'Only possible from Rejected. The timeline will show Rejected → Re-opened, preserving that it was rejected.',
  'fileDetail.reopen.remarks': 'Remarks',
  'fileDetail.reopen.remarks.required': 'State why this file is being re-opened.',
  'fileDetail.reopen.submit': 'Re-open file',
  'fileDetail.reopen.success': 'File re-opened.',
  'fileDetail.reassign.action': 'Reassign',
  'fileDetail.reassign.title': 'Reassign this file',
  'fileDetail.reassign.body': 'For when the current holder cannot act — this does not require you to be the current holder.',
  'fileDetail.reassign.target': 'Reassign to',
  'fileDetail.reassign.reason': 'Reason',
  'fileDetail.reassign.remarks': 'Remarks',
  'fileDetail.reassign.remarks.required': 'State the reason for this reassignment.',
  'fileDetail.reassign.submit': 'Reassign',
  'fileDetail.reassign.success': 'File reassigned.',
  'fileDetail.upload.action': 'Add attachment',
  'fileDetail.upload.success': 'Attachment uploaded.',
  'fileDetail.upload.confidentiality': 'Confidentiality level (optional — defaults to the file\'s own level)',
  'fileDetail.archive.action': 'Archive',
  'fileDetail.archive.confirm': 'Archive this attachment? It will no longer be listed as active (A-006: withdrawn, never deleted).',
  'fileDetail.archive.success': 'Attachment archived.',
  'fileDetail.archive.error': 'Could not archive this attachment.',
  'fileDetail.attachmentPendingScanRetryable': 'Scanning is still in progress — this will update automatically once complete.',
  'fileDetail.conflict.title': 'This file has changed since it was read',
  'fileDetail.conflict.body': 'Reload the file before trying again — nothing was submitted.',
  'fileDetail.conflict.reload': 'Reload this file',

  'common.cancel': 'Cancel',
  'common.dismiss': 'Dismiss',

  'shell.nav.appointments': 'Appointment Inbox',
  'shell.nav.entranceVerification': 'Entrance Verification',
  'shell.nav.myAvailability': 'My Availability',
  'shell.nav.availabilityAdmin': 'Staff Availability',
  'shell.nav.notifications': 'Notifications',

  // Shared 403/500 states (SCREEN_SPECS.md I24)
  'forbidden.back': 'Back to dashboard',
  'forbidden.notFound.title': "This couldn't be found",
  'forbidden.notFound.body': 'It may have been moved, or the link may be incorrect.',
  'forbidden.role.title': "You don't have permission to view this",
  'forbidden.role.body': 'Your role does not include this capability. If you believe this is wrong, contact your section administrator.',
  'forbidden.scope.title': "You don't have permission to view this",
  'forbidden.scope.body': 'This is outside the scope your account is assigned to. If you believe this is wrong, contact your section administrator.',
  'forbidden.ownership.title': "You don't have permission to view this",
  'forbidden.ownership.body': "This record belongs to someone else's queue. If you believe this is wrong, contact your section administrator.",
  'forbidden.clearance.title': "You don't have permission to view this",
  'forbidden.clearance.body': "This record's confidentiality level is above your clearance. If you believe this is wrong, contact your section administrator.",
  'forbidden.generic.title': "You don't have permission to view this",
  'forbidden.generic.body': 'If you believe this is wrong, contact your section administrator.',

  'serverError.title': 'Something went wrong on our side',
  'serverError.body': 'Please try again in a moment.',
  'serverError.correlationId': 'Reference for support: {id}',

  'sessionExpired.title': 'Your session has expired',
  'sessionExpired.body': 'For your security, you were signed out after a period of inactivity. Log in again to continue where you left off.',
  'sessionExpired.action': 'Log in again',

  // I4 — Password reset
  'passwordReset.request.title': 'Reset your password',
  'passwordReset.request.body': 'Enter your ministry code and username and we will send a reset link if the account exists.',
  'passwordReset.request.submit': 'Send reset link',
  'passwordReset.request.submitting': 'Sending',
  'passwordReset.request.success.title': 'If that account exists, a reset link has been sent',
  'passwordReset.request.success.body': 'Check with your Admin/HR contact if you do not receive it — the link expires after a limited time.',
  'passwordReset.request.error.network': 'Something went wrong on our side. Please check your connection and try again.',
  'passwordReset.request.error.generic': 'Something went wrong. Please try again.',
  'passwordReset.complete.title': 'Choose a new password',
  'passwordReset.complete.submit': 'Set new password',
  'passwordReset.complete.submitting': 'Saving',
  'passwordReset.complete.success.title': 'Your password has been changed',
  'passwordReset.complete.success.body': 'You were signed out of {count} other session(s) for your security. Log in with your new password.',
  'passwordReset.complete.expired.title': 'This reset link has expired',
  'passwordReset.complete.expired.body': 'Request a new one below — the previous link can no longer be used.',
  'passwordReset.complete.noToken.title': 'This link is missing its reset token',
  'passwordReset.complete.noToken.body': 'Open the link from your reset email again, or request a new one below.',
  'passwordReset.complete.error.network': 'Something went wrong on our side. Please check your connection and try again.',
  'passwordReset.complete.error.generic': 'Something went wrong. Please try again.',
  'passwordReset.complete.requestNewLink': 'Request a new reset link',

  // I11 — Notification Centre (full page)
  'notifications.centre.title': 'Notifications',
  'notifications.centre.description': 'Every notification sent to your account — assignment, SLA warnings, appointment updates, and mentions.',
  'notifications.centre.unreadOnly': 'Show unread only',
  'notifications.centre.empty.title': 'No notifications yet',
  'notifications.centre.empty.body': "You'll see assignments, SLA warnings, appointment updates and mentions here as they happen.",
  'notifications.centre.empty.unread.title': 'No unread notifications',
  'notifications.centre.empty.unread.body': "You're all caught up. Turn off \"unread only\" to see your full history.",
  'notifications.centre.error.title': "Your notifications couldn't be loaded",
  'notifications.centre.error.body': 'Please try again.',
  'notifications.centre.loadMore': 'Load more',

  // I12 — My Availability
  'availability.my.title': 'My Availability',
  'availability.my.description': 'What citizens see when they look you up on the public directory. Phase 1 is manual — there is no automatic feed yet.',
  'availability.my.current': 'Current status:',
  'availability.my.quickToggle': 'Available now',
  'availability.my.statusLabel': 'Status',
  'availability.my.citizenSees': 'A citizen looking you up sees:',
  'availability.my.noteLabel': 'Internal note (not shown to the public)',
  'availability.my.noteHelper': 'Visible only to staff — e.g. "Back from lunch at 2pm." Never shown on the public directory.',
  'availability.my.save': 'Save',
  'availability.my.saving': 'Saving',
  'availability.my.saved': 'Saved.',
  'availability.my.saveError.title': "Your status couldn't be updated",
  'availability.my.saveError.body': 'It still shows "{status}" publicly.',
  'availability.my.loadError.title': "Your availability couldn't be loaded",
  'availability.my.loadError.body': 'Please try again.',

  // I13 — Availability Administration
  'availability.admin.title': 'Staff Availability',
  'availability.admin.description': 'Every staff member currently visible on the public officer directory, and their status.',
  'availability.admin.caption': 'Staff availability',
  'availability.admin.column.name': 'Name',
  'availability.admin.column.designation': 'Designation',
  'availability.admin.column.orgUnit': 'Section',
  'availability.admin.column.status': 'Status',
  'availability.admin.column.override': 'Set status',
  'availability.admin.empty.title': 'No staff records in this section yet',
  'availability.admin.empty.body': 'Staff availability will appear here once accounts are set up.',
  'availability.admin.error.conflict': "This person's status changed since the page loaded — reloading.",
  'availability.admin.error.generic': "Couldn't update this status. Please try again.",

  // I14 — Appointment Inbox
  'appointments.inbox.title': 'Appointment Inbox',
  'appointments.inbox.description': 'Visit requests made through the public portal — accept, reschedule or decline each one.',
  'appointments.inbox.caption': 'Appointment requests',
  'appointments.statPending': 'Pending requests',
  'appointments.tab.label': 'Filter by status',
  'appointments.tab.all': 'All',
  'appointments.tab.pending': 'Pending',
  'appointments.tab.accepted': 'Accepted',
  'appointments.tab.rescheduled': 'Rescheduled',
  'appointments.tab.declined': 'Declined',
  'appointments.tab.completed': 'Completed',
  'appointments.tab.cancelled': 'Cancelled',
  'appointments.column.when': 'When',
  'appointments.column.status': 'Status',
  'appointments.column.visitor': 'Visitor',
  'appointments.column.purpose': 'Purpose',
  'appointments.column.myAvailability': 'My availability',
  'appointments.column.actions': 'Actions',
  'appointments.action.accept': 'Accept',
  'appointments.action.reschedule': 'Reschedule',
  'appointments.action.decline': 'Decline',
  'appointments.action.cancel': 'Cancel',
  'appointments.empty.pending.title': 'No pending appointment requests',
  'appointments.empty.pending.body': "You're caught up — new requests will appear here.",
  'appointments.empty.filtered.title': 'No appointments match this filter',
  'appointments.empty.filtered.body': 'Try a different status tab.',
  'appointments.forbidden.title': "You don't have permission to view this",
  'appointments.forbidden.body': 'If you believe this is wrong, contact your section administrator.',
  'appointments.submitting': 'Saving',
  'appointments.requestedFor': '{visitor} requested this for: {purpose}',
  'appointments.accept.title': 'Accept this appointment',
  'appointments.accept.description': 'Confirm the date and time — by default, the time the citizen requested.',
  'appointments.accept.submit': 'Accept appointment',
  'appointments.reschedule.title': 'Reschedule this appointment',
  'appointments.reschedule.description': 'Move this appointment to a new date and time. The citizen will see the updated schedule.',
  'appointments.reschedule.submit': 'Save new time',
  'appointments.field.date': 'Date',
  'appointments.field.time': 'Time',
  'appointments.field.time.invalid': 'Enter a time as HH:MM, e.g. 14:30.',
  'appointments.field.rescheduleReason': 'Reason for rescheduling (optional)',
  'appointments.field.rescheduleReason.helper': 'Internal only — not shown to the citizen.',
  'appointments.decline.title': 'Decline this appointment',
  'appointments.decline.description': 'The citizen will be told a bilingual, citizen-safe version of the reason you choose below — never your own words verbatim.',
  'appointments.decline.submit': 'Decline appointment',
  'appointments.decline.reasonCode': 'Reason category',
  'appointments.decline.reasonCode.placeholder': 'Choose a reason',
  'appointments.decline.citizenSees': 'The citizen will see:',
  'appointments.decline.reason': 'Internal note (not shown to the citizen)',
  'appointments.decline.reason.required': 'Enter a reason — a decline cannot be recorded without one.',
  'appointments.decline.reason.helper': 'For your own records and for colleagues who see this later.',
  'appointments.decline.error.slotNotTaken.title': "That time isn't actually taken",
  'appointments.decline.error.slotNotTaken.body': 'No accepted appointment occupies that time, so this cannot be recorded as "time taken" — choose the reason that actually applies.',
  'appointments.cancel.title': 'Cancel this appointment',
  'appointments.cancel.description': 'Use this when the office can no longer keep an already-accepted appointment.',
  'appointments.cancel.submit': 'Cancel appointment',
  'appointments.cancel.reason': 'Reason (optional)',
  'appointments.cancel.reason.helper': 'Internal only.',
  'appointments.error.conflict.title': 'This appointment has changed since it was read',
  'appointments.error.conflict.body': 'Its current status is now "{status}". Reload it before trying again.',
  'appointments.error.staleReload': 'Reload this appointment',
  'appointments.error.slotTaken.title': "That time is already taken",
  'appointments.error.slotTaken.body': 'Another appointment already holds that exact time. Choose a different time.',
  'appointments.error.state.title': "This appointment can't be changed anymore",
  'appointments.error.state.body': 'Its current status no longer allows this action.',
  'appointments.error.ownership.title': "This isn't assigned to you",
  'appointments.error.ownership.body': 'Only the officer this request was made for can act on it.',
  'appointments.error.network': 'Something went wrong on our side. Please check your connection and try again.',
  'appointments.error.generic': 'Something went wrong. Please try again.',

  // I15 — Appointment Detail & Entrance Verification
  'appointments.detail.title': 'Appointment',
  'appointments.detail.visitor': 'Visitor',
  'appointments.detail.when': 'When',
  'appointments.detail.purpose': 'Purpose',
  'appointments.detail.requestedOnly': '(requested time — not yet scheduled)',
  'appointments.detail.myAvailability': 'My availability at the time',
  'appointments.detail.declineReason': 'Decline reason (internal)',
  'appointments.detail.timeline': 'History',
  'appointments.detail.notFound.title': "This appointment couldn't be found",
  'appointments.detail.notFound.body': 'It may have been removed, or the link may be incorrect.',
  'appointments.detail.error.title': "This appointment couldn't be loaded",
  'appointments.detail.error.body': 'Please try again.',
  'appointments.timeline.actorStaff': 'Staff',
  'appointments.timeline.actorPublic': 'Citizen (public portal)',
  'appointments.timeline.actorSystem': 'System',
  'appointments.verify.title': 'Entrance Verification',
  'appointments.verify.description': 'Scan or enter a visitor\'s reference number or QR code to confirm arrival.',
  'appointments.verify.codeLabel': 'Reference number or QR code',
  'appointments.verify.codeHelper': 'e.g. APT-XXXXXXXXXX, from the visitor\'s confirmation.',
  'appointments.verify.codeInvalid': "That doesn't look like a valid reference number or QR code.",
  'appointments.verify.submit': 'Verify',
  'appointments.verify.submitting': 'Verifying',
  'appointments.verify.scanWithCamera': 'Scan with camera',
  'appointments.verify.cameraPreviewLabel': 'Camera preview — point at the visitor\'s QR code',
  'appointments.verify.camera.unsupported': "Camera scanning isn't supported in this browser — enter the reference number instead.",
  'appointments.verify.camera.error': "Couldn't access the camera — enter the reference number instead.",
  'appointments.verify.error.invalidOrUsed': "This code isn't valid or has already been used.",
  'appointments.verify.error.currentStatus': 'Current status: {status}',
  'appointments.verify.error.format': "That doesn't look like a valid reference number or QR code.",
  'appointments.verify.error.network': 'Something went wrong on our side. Please check your connection and try again.',
  'appointments.verify.error.generic': 'Something went wrong. Please try again.',
  'appointments.verify.markCompleted': 'Mark as completed',
  'appointments.verify.completing': 'Saving',
  'appointments.verify.completeError': "Couldn't mark this as completed. Please try again.",
  'appointments.verify.alreadyCompleted': 'This appointment is already marked as completed.',
  'appointments.verify.viewFullDetail': 'View full appointment detail',

  // Toast confirmations (CLAUDE.md brief: "confirm the action visibly ... one
  // mechanism, not a banner here and inline text there"). Every write that used to
  // close its modal and refetch silently now also confirms through `useToast()`.
  'toast.movement.forward': 'File forwarded.',
  'toast.movement.approve': 'File approved.',
  'toast.movement.reject': 'File rejected.',
  'toast.movement.comment': 'Comment added.',
  'toast.file.edited': 'File details updated.',
  'toast.file.darta': 'Darta/Chalani reference saved.',
  'toast.file.classified': 'Confidentiality level updated.',
  'toast.file.reopened': 'File reopened.',
  'toast.file.reassigned': 'File reassigned.',
  'toast.appointments.accepted': 'Appointment accepted.',
  'toast.appointments.rescheduled': 'Appointment rescheduled.',
  'toast.appointments.declined': 'Appointment declined.',
  'toast.appointments.cancelled': 'Appointment cancelled.',
  'toast.appointments.verified': 'Visit marked as completed.',
  'toast.notifications.markedAll': 'All notifications marked as read.',

  // ---- Administration (I19-I22, web/internal/src/routes/admin) ----
  'admin.nav.users': 'Staff Directory',
  'admin.nav.orgUnits': 'Org Structure',
  'admin.nav.tenants': 'Ministries (Platform)',
  'admin.nav.platformAudit': 'Platform Audit Log',
  'admin.common.cancel': 'Cancel',
  'admin.common.retry': 'Retry',
  'admin.common.save': 'Save',
  'admin.common.saving': 'Saving',
  'admin.error.checkFields': 'Please check the highlighted fields.',
  'admin.error.generic': 'Something went wrong. Please try again.',
  'admin.error.network': 'Something went wrong on our side. Please check your connection and try again.',
  'fieldError.required': 'This field is required.',
  'fieldError.invalidType': 'This value is not the right type.',
  'fieldError.invalidFormat': 'This value is not in the expected format.',
  'fieldError.tooShort': 'This value is too short (minimum {min}).',
  'fieldError.tooLong': 'This value is too long (maximum {max}).',
  'fieldError.outOfRange': 'This value is out of the allowed range.',
  'fieldError.notAllowedValue': 'This value is not allowed.',
  'fieldError.unknownField': 'This field is not recognised.',
  'fieldError.generic': 'This value was rejected.',
  'admin.grantRefusal.noGrantAuthority': 'You do not currently hold any role that can grant roles to others.',
  'admin.grantRefusal.selfGrant': 'You cannot grant a role assignment to yourself, whatever the role — a second administrator must make this grant.',
  'admin.grantRefusal.roleAboveActor': 'This role carries administrative permissions you do not hold yourself — you cannot grant a role above your own standing.',
  'admin.grantRefusal.scopeAboveActor': 'The scope you selected is wider than your own administrative scope — you cannot create reach you do not have.',
  'admin.grantRefusal.scopeOutsideActor': 'The org unit you selected is outside the part of the organisation you administer.',
  'admin.grantRefusal.clearanceAboveActor': 'The clearance rank you selected is above your own — you cannot grant a clearance you do not hold.',
  'admin.grantRefusal.generic': 'This grant was refused.',
  'admin.users.title': 'Staff Directory',
  'admin.users.description': 'Manage staff accounts, roles and access across your ministry.',
  'admin.users.newUser': 'New user',
  'admin.users.search.label': 'Search',
  'admin.users.search.placeholder': 'Name or username',
  'admin.users.search.submit': 'Search',
  'admin.users.filter.status.label': 'Status',
  'admin.users.filter.status.all': 'All statuses',
  'admin.users.filter.status.active': 'Active',
  'admin.users.filter.status.suspended': 'Suspended',
  'admin.users.filter.status.deactivated': 'Deactivated',
  'admin.users.column.name': 'Name',
  'admin.users.column.username': 'Username',
  'admin.users.column.orgUnit': 'Org unit',
  'admin.users.column.roles': 'Roles',
  'admin.users.column.status': 'Status',
  'admin.users.noRoles': 'No roles yet',
  'admin.users.caption': 'Staff accounts',
  'admin.users.empty.title': 'No staff accounts yet',
  'admin.users.empty.body': 'Create the first staff account with "New user" above.',
  'admin.users.emptyFiltered.title': 'No staff match this search',
  'admin.users.emptyFiltered.body': 'Try a different name, username, or status filter.',
  'admin.userCreate.title': 'New staff account',
  'admin.userCreate.description': 'Create a staff account. A role is granted separately, from the account\'s own page.',
  'admin.userCreate.username.label': 'Username',
  'admin.userCreate.username.hint': '3-64 characters: letters, numbers, dots, underscores or hyphens.',
  'admin.userCreate.username.error': 'Enter a valid username (3-64 characters).',
  'admin.userCreate.fullName.label': 'Full name',
  'admin.userCreate.fullName.error': 'Full name is required.',
  'admin.userCreate.password.label': 'Temporary password',
  'admin.userCreate.password.hint': 'At least {min} characters. The account holder must change this on first login.',
  'admin.userCreate.password.rejected.too_short': 'The temporary password must be at least {min} characters.',
  'admin.userCreate.password.rejected.too_long': 'The temporary password must be at most {max} characters.',
  'admin.userCreate.password.rejected.blocked_term': 'This contains a term that is too common to be safe. Choose something less guessable.',
  'admin.userCreate.password.rejected.contains_username': 'The temporary password must not contain the username.',
  'admin.userCreate.password.error': 'A temporary password is required.',
  'admin.userCreate.email.label': 'Email (optional)',
  'admin.userCreate.phone.label': 'Phone (optional)',
  'admin.userCreate.locale.label': 'Preferred language',
  'admin.userCreate.locale.ne': 'Nepali',
  'admin.userCreate.locale.en': 'English',
  'admin.userCreate.orgUnit.label': 'Org unit',
  'admin.userCreate.orgUnit.error': 'Choose the unit this person belongs to. An account with no unit cannot hold a file and would not appear in your staff directory.',
  'admin.userCreate.submit': 'Create account',
  'admin.userCreate.submitting': 'Creating',
  'admin.userCreate.toast.success': '{name}\'s account was created.',
  'admin.userCreate.error.duplicateUsername': 'That username is already taken in this ministry.',
  'admin.orgUnitPicker.manualEntryHelp': 'You do not have permission to browse the org tree here — enter the org unit\'s id directly.',
  'admin.orgUnitPicker.loadError': 'Could not load org units. You can still type the org unit\'s id.',
  'admin.userDetail.title': 'Staff account',
  'admin.userDetail.loading': 'Loading staff account',
  'admin.userDetail.backToDirectory': 'Back to Staff Directory',
  'admin.userDetail.error.title': 'Could not load this account',
  'admin.userDetail.error.body': 'Something went wrong loading this account. Please try again.',
  'admin.userDetail.profile.title': 'Profile',
  'admin.userDetail.profile.edit': 'Edit',
  'admin.userDetail.profile.editTitle': 'Edit profile',
  'admin.userDetail.profile.email': 'Email',
  'admin.userDetail.profile.locale': 'Preferred language',
  'admin.userDetail.profile.mfa': 'Two-factor authentication',
  'admin.userDetail.profile.mfaOn': 'Enabled',
  'admin.userDetail.profile.mfaOff': 'Not set up',
  'admin.userDetail.profile.lastLogin': 'Last login',
  'admin.userDetail.profile.never': 'Never',
  'admin.userDetail.profile.conflict': 'This account changed since you loaded it. Reload and try again.',
  'admin.userDetail.toast.saved': 'Profile updated.',
  'admin.userDetail.status.title': 'Account status',
  'admin.userDetail.deactivate.action': 'Deactivate account',
  'admin.userDetail.deactivate.selfNote': 'You cannot deactivate your own account.',
  'admin.userDetail.roles.title': 'Role assignments',
  'admin.userDetail.roles.empty.title': 'No role assignments yet',
  'admin.userDetail.roles.empty.body': 'This account cannot access anything until a role is granted.',
  'admin.userDetail.roles.clearance': 'Clearance rank {rank}',
  'admin.userDetail.roles.validFrom': 'Since {date}',
  'admin.userDetail.roles.validTo': 'Until {date}',
  'admin.userDetail.sessions.title': 'Sessions',
  'admin.userDetail.sessions.count': '{count} active session(s).',
  'admin.userDetail.sessions.manage': 'Manage sessions',
  'admin.grant.action': 'Grant role',
  'admin.grant.selfNote': 'You cannot grant a role to yourself — ask a second administrator.',
  'admin.grant.title': 'Grant a role to {name}',
  'admin.grant.description': 'Choose the role, its scope, an org-unit anchor if the scope needs one, and a clearance rank.',
  'admin.grant.submit': 'Grant',
  'admin.grant.submitting': 'Granting',
  'admin.grant.role.label': 'Role',
  'admin.grant.role.placeholder': 'Select a role',
  'admin.grant.role.error': 'Select a role.',
  'admin.grant.scope.label': 'Scope',
  'admin.grant.scope.placeholder': 'Select a scope',
  'admin.grant.scope.error': 'Select a scope.',
  'admin.grant.scope.hint': 'How wide this role can see and act.',
  'admin.grant.scope.platformOnlySuperAdmin': 'Only the Super Admin role can be granted platform-wide scope.',
  'admin.grant.anchor.label': 'Org-unit anchor',
  'admin.grant.anchor.hint': 'Required for this scope — the position in the org tree this assignment is anchored to.',
  'admin.grant.anchor.error': 'An org unit is required for this scope.',
  'admin.grant.clearance.label': 'Clearance rank',
  'admin.grant.clearance.placeholder': 'Select a clearance rank',
  'admin.grant.clearance.error': 'Select a clearance rank.',
  'admin.grant.error.catalogueLoad': 'Could not load the role list. Please retry.',
  'admin.grant.error.noneGrantable': 'You do not currently hold a role that lets you grant any role.',
  'admin.grant.error.userNotFound': 'This account could not be found.',
  'admin.grant.toast.success': '{role} granted.',
  'admin.grant.mfaTicket.title': 'One-time authenticator setup link',
  'admin.grant.mfaTicket.body': 'This role requires two-factor sign-in. Enrolment token: {token} (expires {expiresAt}). Hand this to the account holder — it will not be shown again.',
  'admin.revoke.action': 'Revoke',
  'admin.revoke.title': 'Revoke this role assignment?',
  'admin.revoke.description': 'This removes {role} ({scope}) from {name}. This takes effect on their next request.',
  'admin.revoke.submit': 'Revoke',
  'admin.revoke.submitting': 'Revoking',
  'admin.revoke.toast.success': 'Role assignment revoked.',
  'admin.revoke.error.alreadyEnded': 'That assignment has already ended.',
  'admin.deactivate.title': 'Deactivate {name}\'s account?',
  'admin.deactivate.description': 'They will be signed out immediately and will not be able to log in. This cannot be undone from this screen.',
  'admin.deactivate.reason.label': 'Reason',
  'admin.deactivate.reason.error': 'Enter a reason (at least 3 characters).',
  'admin.deactivate.submit': 'Deactivate',
  'admin.deactivate.submitting': 'Deactivating',
  'admin.deactivate.toast.success': '{name}\'s account was deactivated.',
  'admin.deactivate.error.stillActive': 'The account is still active — nothing was changed.',
  'admin.deactivate.error.selfOrConflict': 'This account cannot be deactivated right now — it is still active.',
  'admin.deactivate.error.notFound': 'This account could not be found. It is still active.',
  'admin.sessions.title': 'Session management',
  'admin.sessions.breadcrumb': 'Sessions',
  'admin.sessions.description': '{name}\'s active sessions — device, IP address and last activity.',
  'admin.sessions.caption': 'Active sessions',
  'admin.sessions.column.device': 'Device',
  'admin.sessions.column.ip': 'IP address',
  'admin.sessions.column.lastSeen': 'Last active',
  'admin.sessions.column.since': 'Signed in since',
  'admin.sessions.column.actions': 'Actions',
  'admin.sessions.current': 'This session',
  'admin.sessions.unknownDevice': 'Unknown device',
  'admin.sessions.empty.title': 'No active sessions',
  'admin.sessions.empty.body': 'This account is not currently signed in anywhere.',
  'admin.sessions.revokeOne': 'Sign out',
  'admin.sessions.revokeAll': 'Sign out everywhere',
  'admin.sessions.revoking': 'Signing out',
  'admin.sessions.revokeAllReason': 'Administrative sign-out of all sessions.',
  'admin.sessions.revokeOneConfirm.title': 'Sign out this session?',
  'admin.sessions.revokeOneConfirm.description': 'This ends one device\'s access for {name} immediately.',
  'admin.sessions.revokeAllConfirm.title': 'Sign out {name} everywhere?',
  'admin.sessions.revokeAllConfirm.description': 'This ends every active session for {name} immediately, on every device.',
  'admin.sessions.toast.revokedOne': 'Session signed out.',
  'admin.sessions.toast.revokedAll': '{count} session(s) signed out.',
  'admin.sessions.error.stillActiveOne': 'Could not sign out that session — it is still active.',
  'admin.sessions.error.stillActiveAll': 'Could not sign out all sessions — some may still be active.',
  'admin.org.title': 'Organization Structure',
  'admin.org.description': 'The ministry → division → section tree that role scopes and file sections are built on.',
  'admin.org.newMinistry': 'New top-level unit',
  'admin.org.includeArchived': 'Show archived units',
  'admin.org.empty.title': 'No org units yet',
  'admin.org.empty.body': 'This is a genuine first-run state — create the ministry\'s first unit above.',
  'admin.org.error.title': 'Could not load the org tree',
  'admin.org.error.body': 'Something went wrong loading the org units. Please try again.',
  'admin.org.addChild': 'Add {kind}',
  'admin.org.rename.action': 'Rename',
  'admin.org.archive.action': 'Archive',
  'admin.org.toast.created': '{name} was created.',
  'admin.org.toast.renamed': '{name} was updated.',
  'admin.org.toast.archived': '{name} was archived.',
  'admin.org.create.title': 'New {kind}',
  'admin.org.create.descriptionWithParent': 'This will be created under {parent}.',
  'admin.org.create.descriptionRoot': 'This creates a new top-level unit.',
  'admin.org.create.submit': 'Create',
  'admin.org.create.submitting': 'Creating',
  'admin.org.create.code.label': 'Code',
  'admin.org.create.code.error': 'A code is required.',
  'admin.org.create.nameNe.label': 'Name (Nepali)',
  'admin.org.create.nameNe.error': 'The Nepali name is required.',
  'admin.org.create.nameEn.label': 'Name (English)',
  'admin.org.create.nameEn.error': 'The English name is required.',
  'admin.org.create.error.duplicateCode': 'That code is already used in this ministry.',
  'admin.org.create.error.badParent': 'That parent unit is not valid for this kind of unit.',
  'admin.org.rename.title': 'Rename {name}',
  'admin.org.rename.submit': 'Save',
  'admin.org.rename.submitting': 'Saving',
  'admin.org.rename.error.conflict': 'This unit changed since you loaded it. Reload and try again.',
  'admin.org.archive.title': 'Archive {name}?',
  'admin.org.archive.description': 'Archived units are hidden from pickers but never deleted. This can only be done while the unit is empty.',
  'admin.org.archive.submit': 'Archive',
  'admin.org.archive.submitting': 'Archiving',
  'admin.org.archive.error.notFound': 'This unit could not be found.',
  'admin.org.archive.error.notEmpty.title': 'This unit still has live files, staff or sub-units',
  'admin.org.archive.error.notEmpty.body': '{liveFiles} live file(s), {activeStaff} active staff and {liveChildren} sub-unit(s) are still attached. Move or resolve them before archiving.',
  'admin.tenant.title': 'Ministries',
  'admin.tenant.description': 'Onboard and manage every ministry on this platform.',
  'admin.tenant.newTenant': 'Onboard ministry',
  'admin.tenant.caption': 'Ministries',
  'admin.tenant.column.name': 'Name',
  'admin.tenant.column.prefix': 'Tracking prefix',
  'admin.tenant.column.onboardedAt': 'Onboarded',
  'admin.tenant.column.status': 'Status',
  'admin.tenant.column.actions': 'Actions',
  'admin.tenant.empty.title': 'Only this ministry exists so far',
  'admin.tenant.empty.body': 'Nothing is broken — no other ministry has been onboarded onto this platform yet.',
  'admin.tenant.status.active': 'Active',
  'admin.tenant.status.suspended': 'Suspended',
  'admin.tenant.edit.action': 'Edit',
  'admin.tenant.edit.title': 'Edit {name}',
  'admin.tenant.edit.status.label': 'Status',
  'admin.tenant.edit.suspendWarning': 'Suspending a ministry blocks every staff login for it immediately.',
  'admin.tenant.toast.updated': '{name} was updated.',
  'admin.tenant.toast.onboarded': '{name} was onboarded.',
  'admin.tenant.create.title': 'Onboard a new ministry',
  'admin.tenant.create.description': 'Creates the tenant, its tracking-code prefix, its root org unit and its first Secretary account.',
  'admin.tenant.create.submit': 'Onboard',
  'admin.tenant.create.submitting': 'Onboarding',
  'admin.tenant.create.required': 'This field is required.',
  'admin.tenant.create.prefix.label': 'Tracking-code prefix',
  'admin.tenant.create.prefix.error': 'Enter 2-6 letters only.',
  'admin.tenant.create.prefix.hint': 'This cannot be changed once the first file is registered under it. Tracking codes will look like {prefix}-2083-000001.',
  'admin.tenant.create.error.duplicatePrefix': 'That prefix has already been allocated, even to a ministry that has since been archived.',
  'admin.tenant.create.nameNe.label': 'Ministry name (Nepali)',
  'admin.tenant.create.nameEn.label': 'Ministry name (English)',
  'admin.tenant.create.rootOrgUnitCode.label': 'Root org-unit code',
  'admin.tenant.create.rootOrgUnitCode.hint': 'e.g. HQ — the top of this ministry\'s own org tree.',
  'admin.tenant.create.secretary.heading': 'First Secretary account',
  'admin.tenant.create.secretary.username.label': 'Username',
  'admin.tenant.create.secretary.fullName.label': 'Full name',
  'admin.tenant.create.secretary.password.label': 'Temporary password',
  'admin.tenant.create.mfaTicket.body': 'Secretary accounts require two-factor sign-in. Enrolment token for {username}: {token} (expires {expiresAt}). Hand this to them out of band — it will not be shown again.',
  'admin.audit.title': 'Platform Audit Log',
  'admin.audit.description': 'Every cross-tenant administrative action on this platform. Read-only — nothing here can be edited or deleted.',
  'admin.audit.caption': 'Platform audit events',
  'admin.audit.column.occurredAt': 'When',
  'admin.audit.column.category': 'Category',
  'admin.audit.column.action': 'Action',
  'admin.audit.column.outcome': 'Outcome',
  'admin.audit.column.subject': 'Subject',
  'admin.audit.column.crossTenant': 'Cross-tenant',
  'admin.audit.crossTenantYes': 'Yes',
  'admin.audit.crossTenantNo': 'No',
  'admin.audit.filter.category': 'Category',
  'admin.audit.filter.action': 'Action',
  'admin.audit.filter.from': 'From',
  'admin.audit.filter.to': 'To',
  'admin.audit.filter.limit': 'Rows',
  'admin.audit.filter.apply': 'Apply filters',
  'admin.audit.truncated': 'Showing the latest {limit} matching events. Narrow the filters above to see fewer, more specific results.',
  'admin.audit.empty.title': 'No matching audit events',
  'admin.audit.empty.body': 'No platform-level administrative action matches these filters.',
};

const ne: Dict = {
  'login.title': 'कर्मचारी लगइन',
  'login.ministry.label': 'मन्त्रालय कोड',
  'login.ministry.hint': 'तपाईंको ट्र्याकिङ कोडमा रहेको २-६ अक्षरको उपसर्ग, जस्तै MOHA।',
  'login.ministry.invalid': '२-६ अक्षरको मन्त्रालय कोड लेख्नुहोस्।',
  'login.username.label': 'प्रयोगकर्ता नाम वा इमेल',
  'login.password.label': 'पासवर्ड',
  'login.showPassword': 'पासवर्ड देखाउनुहोस्',
  'login.hidePassword': 'पासवर्ड लुकाउनुहोस्',
  'login.submit': 'लगइन गर्नुहोस्',
  'login.submitting': 'लगइन हुँदैछ',
  'login.forgotPassword': 'पासवर्ड बिर्सनुभयो?',
  'login.required': 'यो फिल्ड आवश्यक छ।',
  'login.error.invalidCredentials': 'गलत प्रयोगकर्ता नाम वा पासवर्ड।',
  'login.error.locked': 'यो खाता अस्थायी रूपमा लक गरिएको छ। {minutes} मिनेटमा पुनः प्रयास गर्नुहोस्, वा एडमिन/एचआरलाई सम्पर्क गर्नुहोस्।',
  'login.error.rateLimited': 'धेरै पटक प्रयास भयो। कृपया केही समय पर्खेर पुनः प्रयास गर्नुहोस्।',
  'login.error.network': 'हाम्रोतर्फ केही समस्या भयो। कृपया आफ्नो इन्टरनेट जाँचेर पुनः प्रयास गर्नुहोस्।',
  'login.error.generic': 'केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'common.retry': 'पुनः प्रयास गर्नुहोस्',
  'mfa.title': 'प्रमाणीकरण कोड',
  'mfa.instructions': 'आफ्नो प्रमाणीकरण एपबाट ६ अंकको कोड प्रविष्ट गर्नुहोस्।',
  'mfa.codeLabel': '६ अंकको कोड',
  'mfa.submit': 'प्रमाणित गर्नुहोस्',
  'mfa.submitting': 'प्रमाणित हुँदैछ',
  'mfa.error.invalid': 'गलत कोड। पुनः प्रयास गर्नुहोस्।',
  'mfa.error.rateLimited': 'धेरै पटक प्रयास भयो। कृपया केही समय पर्खेर पुनः प्रयास गर्नुहोस्।',
  'mfa.error.network': 'हाम्रोतर्फ केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'mfa.error.generic': 'केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'mfa.contactAdmin': 'उपकरण हरायो? एडमिन/एचआरलाई सम्पर्क गर्नुहोस् — यो आफैं रिसेट गर्न मिल्दैन।',
  'mfa.backToLogin': 'लगइनमा फर्कनुहोस्',

  // ---- MFA enrolment (R-033) --------------------------------------------------------
  'mfaEnrol.title': 'आफ्नो प्रमाणीकरण एप सेटअप गर्नुहोस्',
  'mfaEnrol.noToken.title': 'यो लिङ्कमा नामांकन टोकन छैन',
  'mfaEnrol.noToken.body': 'प्रशासकले दिएको लिङ्क फेरि खोल्नुहोस्, वा नयाँ लिङ्कको लागि उहाँलाई सम्पर्क गर्नुहोस्।',
  'mfaEnrol.loading': 'तपाईंको नामांकन विवरण लोड हुँदैछ',
  'mfaEnrol.tokenInvalid.title': 'यो नामांकन लिङ्क अब मान्य छैन',
  'mfaEnrol.tokenInvalid.body': 'यो म्याद सकिएको, पहिले नै प्रयोग गरिएको, वा गलत टाइप भएको हुन सक्छ। नयाँ नामांकन लिङ्कको लागि प्रशासकलाई सम्पर्क गर्नुहोस् — यो पुनः प्रयास गरेर ठीक हुँदैन।',
  'mfaEnrol.error.network': 'हाम्रोतर्फ केही समस्या भयो। कृपया आफ्नो इन्टरनेट जाँचेर पुनः प्रयास गर्नुहोस्।',
  'mfaEnrol.error.generic': 'केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'mfaEnrol.instructions': '{username} का लागि आफ्नो प्रमाणीकरण एप (जस्तै Google Authenticator, Authy) बाट यो कोड स्क्यान गर्नुहोस्, त्यसपछि तल यसले देखाउने ६ अंकको कोड प्रविष्ट गर्नुहोस्।',
  'mfaEnrol.qrLabel': 'दुई-चरण लगइन सेटअप गर्न आफ्नो प्रमाणीकरण एपले स्क्यान गर्नुहोस्',
  'mfaEnrol.secretLabel': 'स्क्यान गर्न सक्नुहुन्न? यसको सट्टा यो कुञ्जी म्यानुअल रूपमा प्रविष्ट गर्नुहोस्:',
  'mfaEnrol.secretCopy': 'प्रतिलिपि गर्नुहोस्',
  'mfaEnrol.secretCopied': 'प्रतिलिपि गरियो',
  'mfaEnrol.codeLabel': '६ अंकको कोड',
  'mfaEnrol.confirmSubmit': 'सक्रिय गर्नुहोस्',
  'mfaEnrol.confirming': 'सक्रिय हुँदैछ',
  'mfaEnrol.confirm.error.invalid': 'गलत कोड। आफ्नो प्रमाणीकरण एप जाँचेर पुनः प्रयास गर्नुहोस्।',
  'mfaEnrol.confirmed.title': 'तपाईंको प्रमाणीकरण एप सेटअप भयो',
  'mfaEnrol.confirmed.body': 'अब तपाईं आफ्नो प्रयोगकर्ता नाम, पासवर्ड, र प्रमाणीकरण एपको कोड प्रयोग गरेर लगइन गर्न सक्नुहुन्छ।',
  'mfaEnrol.confirmed.goToLogin': 'लगइनमा जानुहोस्',
  'shell.dashboard': 'ड्यासबोर्ड',
  'shell.nav.label': 'मुख्य नेभिगेसन',
  'shell.nav.administration': 'प्रशासन',
  'common.notSet': 'तोकिएको छैन',
  'shell.nav.openMenu': 'नेभिगेसन मेनु खोल्नुहोस्',
  'shell.nav.files': 'फाइल रजिस्टर',
  'shell.nav.fileNew': 'फाइल दर्ता',
  'shell.nav.tasks': 'मेरा कार्यहरू',
  'shell.nav.account': 'खाता सेटिङ',
  'shell.systemName': 'मन्त्रालय फाइल ट्र्याकिङ, कार्यप्रवाह र कार्यालय भ्रमण व्यवस्थापन प्रणाली',
  'shell.logout': 'लगआउट',
  'shell.loggingOut': 'लगआउट हुँदैछ',
  'shell.theme.toggle': 'रङ थिम बदल्नुहोस्',
  'shell.theme.light': 'उज्यालो',
  'shell.theme.dark': 'अँध्यारो',
  'shell.theme.system': 'प्रणाली अनुसार',
  'shell.theme.switchToLight': 'उज्यालो थिममा बदल्नुहोस्',
  'shell.theme.switchToDark': 'अँध्यारो थिममा बदल्नुहोस्',
  'shell.locale.toggle': 'भाषा बदल्नुहोस्',
  'shell.locale.nepali': 'नेपाली',
  'shell.locale.english': 'अंग्रेजी',
  'shell.locale.switchTo': 'भाषा: {current}। {next} मा बदल्नुहोस्।',
  'shell.notifications': 'सूचनाहरू (यस संस्करणमा उपलब्ध छैन)',
  'shell.skipToContent': 'मुख्य सामग्रीमा जानुहोस्',
  'shell.sessionLoading': 'तपाईंको सत्र जाँच गर्दै',
  'shell.mustChangePassword': 'अगाडि बढ्नु अघि तपाईंको खाताको पासवर्ड परिवर्तन गर्नुपर्छ।',
  'shell.mustChangePassword.action': 'अहिले पासवर्ड परिवर्तन गर्नुहोस्',
  'shell.account.menuLabel': '{name} को लागि खाता मेनु',
  'shell.account.roleSummary': '{name}, {role}',
  'shell.account.settings': 'खाता सेटिङ',
  'shell.account.logout': 'लगआउट',
  'accountSettings.title': 'खाता सेटिङ',
  'accountSettings.identity.title': 'लगइन गरिएको खाता',
  'accountSettings.language.title': 'भाषा',
  'accountSettings.language.body': 'तुरुन्तै लागू हुन्छ, र यो माथिल्लो पट्टीको भाषा बटन जस्तै नियन्त्रण हो।',
  'accountSettings.theme.title': 'रङ थिम',
  'accountSettings.theme.body': 'तुरुन्तै लागू हुन्छ, र यो माथिल्लो पट्टीको थिम बटन जस्तै नियन्त्रण हो।',
  'accountSettings.password.title': 'पासवर्ड परिवर्तन गर्नुहोस्',
  'accountSettings.password.body': 'नयाँ पासवर्ड छान्नुहोस्। तपाईंलाई हालको पासवर्ड चाहिन्छ।',
  'accountSettings.password.current': 'हालको पासवर्ड',
  'accountSettings.password.new': 'नयाँ पासवर्ड',
  'accountSettings.password.confirm': 'नयाँ पासवर्ड पुष्टि गर्नुहोस्',
  'accountSettings.password.submit': 'पासवर्ड परिवर्तन गर्नुहोस्',
  'accountSettings.password.submitting': 'पासवर्ड परिवर्तन हुँदैछ…',
  'accountSettings.password.success': 'तपाईंको पासवर्ड परिवर्तन भयो।',
  'accountSettings.password.forcedNotice': 'अगाडि बढ्नु अघि तपाईंको खाताको पासवर्ड परिवर्तन गर्नुपर्छ।',
  'accountSettings.password.error.wrongCurrent': 'तपाईंको हालको पासवर्ड गलत छ।',
  'accountSettings.password.error.network': 'हाम्रोतर्फ केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'accountSettings.password.error.generic': 'तपाईंको पासवर्ड परिवर्तन गर्न सकिएन। कृपया पुनः प्रयास गर्नुहोस्।',
  'accountSettings.password.notAvailable.title': 'यस सर्भरमा अहिलेसम्म उपलब्ध छैन',
  'accountSettings.password.notAvailable.body':
    'स्वयं-सेवा पासवर्ड परिवर्तन तोकिएको छ तर यस सर्भरमा अझै लागू गरिएको छैन। पासवर्ड रिसेट गर्न एडमिन/एचआरलाई सम्पर्क गर्नुहोस्।',
  'accountSettings.password.rule.length': '{min}–{max} अक्षर',
  'accountSettings.password.rule.notCommon': 'सामान्य शब्द (जस्तै "password", "ministry") समावेश छैन',
  'accountSettings.password.rule.notUsername': 'तपाईंको प्रयोगकर्ता नाम समावेश छैन',
  'accountSettings.password.rule.mismatch': 'पासवर्डहरू मिलेनन्।',
  'accountSettings.password.rule.serverRejected.too_short': 'धेरै छोटो।',
  'accountSettings.password.rule.serverRejected.too_long': 'धेरै लामो।',
  'accountSettings.password.rule.serverRejected.blocked_term': 'सामान्य निषेधित शब्द समावेश छ।',
  'accountSettings.password.rule.serverRejected.contains_username': 'तपाईंको प्रयोगकर्ता नाम समावेश छ।',
  'accountSettings.notBuilt.title': 'यस चरणमा निर्माण गरिएको छैन',
  'accountSettings.notBuilt.body':
    'MFA दर्ता, र सूचना-च्यानल प्राथमिकताहरू SCREEN_SPECS.md को I23 मा तोकिएका छन् तर यस निर्माणमा कुनै ब्याकइन्ड एन्डपोइन्टसँग जोडिएका छैनन्।',
  'dashboard.title': 'गृह',
  'dashboard.welcome': 'स्वागत छ, {name}',
  'dashboard.welcomeWithRole': 'स्वागत छ, {name} · {role}',
  'dashboard.body': 'तपाईं लगइन हुनुभएको छ। यस कार्यक्षेत्रको बाँकी भाग यस चरणमा निर्माण गरिएको छैन।',
  'dashboard.roles': 'भूमिकाहरू: {roles}',
  'dashboard.myFiles.title': 'तपाईंको दायरामा रहेका फाइलहरू',
  'dashboard.overdue.title': 'म्याद नाघेको',
  'dashboard.dueSoon.title': '३ दिनभित्र म्याद पुग्ने',
  'dashboard.pendingAppointments.title': 'तपाईंको निर्णय पर्खिरहेका भेटघाटहरू',
  'dashboard.stageChart.title': 'चरण अनुसार खुला फाइलहरू',
  'dashboard.stageChart.statusColumn': 'स्थिति',
  'dashboard.stageChart.countColumn': 'फाइल संख्या',
  'dashboard.tasksTitle': 'मेरा कार्यहरू',
  'dashboard.tasksViewAll': 'सबै कार्यहरू हेर्नुहोस्',
  'dashboard.tasksEmpty.title': 'अहिले कुनै फाइल जिम्मा दिइएको छैन',
  'dashboard.tasksEmpty.body': 'तपाईं अद्यावधिक हुनुहुन्छ।',
  'dashboard.noFileAccess.title': 'अहिलेसम्म देखाउन केही छैन',
  'dashboard.noFileAccess.body': 'तपाईंको खातामा यस संस्करणमा फाइल-रजिस्टर पहुँच छैन।',
  'dashboard.tasksError': 'तपाईंको कार्य सूची लोड गर्न सकिएन।',
  'common.you': 'तपाईं',
  'common.unassigned': 'जिम्मा नदिइएको',
  'common.notAvailable': '—',
  'common.clearFilters': 'फिल्टर हटाउनुहोस्',
  'common.viewAll': 'सबै हेर्नुहोस्',
  'common.somethingWentWrong': 'केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'fileRegister.title': 'फाइल रजिस्टर',
  'fileRegister.resultCount': 'तपाईंको दायरामा {count} फाइल',
  'fileRegister.searchLabel': 'विषय वा ट्र्याकिङ कोडबाट खोज्नुहोस्',
  'fileRegister.searchPlaceholder': 'जस्तै MW-2083-000001',
  'fileRegister.filterStatus': 'स्थिति',
  'fileRegister.filterStatusAll': 'सबै स्थिति',
  'fileRegister.filterConfidentiality': 'गोपनीयता',
  'fileRegister.filterConfidentialityAll': 'सबै तह',
  'fileRegister.filterOverdueOnly': 'म्याद नाघेका मात्र',
  'fileRegister.clearFilters': 'फिल्टर हटाउनुहोस्',
  'fileRegister.caption': 'तपाईंको दायरामा रहेका फाइलहरू',
  'fileRegister.column.trackingCode': 'ट्र्याकिङ कोड',
  'fileRegister.column.status': 'स्थिति',
  'fileRegister.column.overdue': 'म्याद नाघेको',
  'fileRegister.column.subject': 'विषय',
  'fileRegister.column.dueDate': 'म्याद',
  'fileRegister.column.assignedTo': 'जिम्मा',
  'fileRegister.assignedToOther': 'जिम्मा दिइएको (नाम यहाँ देखिँदैन)',
  'fileRegister.column.section': 'शाखा',
  'fileRegister.column.registeredDate': 'दर्ता मिति (वि.सं.)',
  'fileRegister.column.confidentiality': 'गोपनीयता',
  'fileRegister.subjectRedacted': 'लुकाइएको — तपाईंको स्वीकृति तह भन्दा माथि',
  'fileRegister.noDueDate': 'म्याद तोकिएको छैन',
  'fileRegister.emptyScope.title': 'तपाईंको दायरामा अहिलेसम्म कुनै फाइल छैन',
  'fileRegister.emptyScope.body': 'तपाईंको शाखामा दर्ता भएका फाइलहरू यहाँ देखिनेछन्।',
  'fileRegister.emptyFiltered.title': 'तपाईंको फिल्टरसँग मिल्ने फाइल भेटिएन',
  'fileRegister.emptyFiltered.body': 'खोज फराकिलो बनाउनुहोस् वा फिल्टर हटाउनुहोस्।',
  'fileRegister.forbidden.title': 'तपाईं फाइल रजिस्टर हेर्न सक्नुहुन्न',
  'fileRegister.forbidden.role.body':
    'तपाईंको भूमिकामा फाइल-रजिस्टर पहुँच छैन। यदि यो गलत हो भने आफ्नो शाखा प्रशासकलाई सम्पर्क गर्नुहोस्।',
  'fileRegister.forbidden.scope.body': 'यो रजिस्टर तपाईंलाई तोकिएको संगठनात्मक दायरा भन्दा बाहिर छ।',
  'fileRegister.bulkAction.selectedCount': '{count} फाइल(हरू) चयन गरियो',
  'fileRegister.bulkAction.reassign': 'चयनित पुनः तोक्नुहोस्',
  'fileRegister.bulkAction.clearSelection': 'चयन हटाउनुहोस्',
  'fileRegister.bulkReassign.title': 'चयनित फाइलहरू पुनः तोक्नुहोस्',
  'fileRegister.bulkReassign.body': '{count} फाइल(हरू) पुनः तोकिँदैछ। हालको धारक कारबाही गर्न नसक्दा प्रयोग गर्नुहोस्।',
  'fileRegister.bulkReassign.submit': 'फाइलहरू पुनः तोक्नुहोस्',
  'fileRegister.bulkReassign.targetHelper':
    'यी फाइलहरू पुनः तोक्ने प्रयोगकर्ता ID (यस निर्माणमा बल्क पुनः तोकाइको लागि कुनै छनोट सूची उपलब्ध छैन — ह्यान्डब्याक हेर्नुहोस्)।',
  'fileRegister.bulkReassign.someUnavailable': 'यी फाइलहरूको हालको संस्करण लोड गर्न सकिएन — यिनीहरूलाई छोडिनेछ:',
  'fileRegister.bulkReassign.resultSummary': '{succeeded} सफल, {failed} असफल',
  'fileRegister.bulkReassign.itemOk': 'पुनः तोकियो',
  'fileRegister.bulkReassign.itemFailed': 'असफल',
  'fileDetail.back': 'फाइल रजिस्टरमा फर्कनुहोस्',
  'fileDetail.loading': 'फाइल लोड हुँदैछ…',
  'fileDetail.tabOverview': 'सारांश',
  'fileDetail.tabHistory': 'इतिहास',
  'fileDetail.tabAttachments': 'संलग्नकहरू',
  'fileDetail.tabDarta': 'दर्ता-चलानी',
  'fileDetail.registeredOn': 'दर्ता मिति {date}',
  'fileDetail.section': 'शाखा: {name}',
  'fileDetail.dueOn': 'म्याद {date}',
  'fileDetail.noDueDate': 'म्याद तोकिएको छैन',
  'fileDetail.applicantTitle': 'निवेदक',
  'fileDetail.applicantPhone': 'फोन',
  'fileDetail.applicantEmail': 'इमेल',
  'fileDetail.applicantAddress': 'ठेगाना',
  'fileDetail.applicantNone': 'कुनै निवेदक विवरण दर्ता छैन।',
  'fileDetail.notProvided': 'उपलब्ध छैन',
  'fileDetail.dartaNumber': 'दर्ता नम्बर',
  'fileDetail.chalaniNumber': 'चलानी नम्बर',
  'fileDetail.dartaRegisterDate': 'दर्ता मिति',
  'fileDetail.dartaNotLinked': 'यस फाइलमा अहिलेसम्म कुनै दर्ता-चलानी सन्दर्भ जोडिएको छैन।',
  'fileDetail.attachmentsEmpty.title': 'कुनै संलग्नक छैन',
  'fileDetail.attachmentsEmpty.body': 'यस फाइलमा अहिलेसम्म कुनै कागजात संलग्न गरिएको छैन।',
  'fileDetail.attachmentsError.title': 'संलग्नकहरू लोड गर्न सकिएन',
  'fileDetail.attachmentPendingScan': 'स्क्यान विचाराधीन — अहिले उपलब्ध छैन',
  'fileDetail.attachmentAboveInternal': 'आन्तरिक भन्दा माथि — अधिकृत डाउनलोड आवश्यक',
  'fileDetail.attachmentDownload': 'डाउनलोड गर्नुहोस्',
  'fileDetail.preview.action': 'पूर्वावलोकन',
  'fileDetail.preview.close': 'बन्द गर्नुहोस्',
  'fileDetail.preview.downloadHint': 'तपाईंको ब्राउजरमा पूर्वावलोकन गरिएको — अन्य कुनै पनि प्रयोजनका लागि डाउनलोड गर्नुहोस्।',
  'fileDetail.preview.pdfTitle': '{name} को पूर्वावलोकन',
  'fileDetail.preview.imageAlt': '{name} को पूर्वावलोकन',
  'fileDetail.preview.unsupported.title': 'यस फाइल प्रकारको लागि पूर्वावलोकन उपलब्ध छैन',
  'fileDetail.preview.unsupported.body': 'यहाँ केवल PDF र छवि संलग्नकहरू मात्र पूर्वावलोकन गर्न सकिन्छ। यो फाइल खोल्न डाउनलोड प्रयोग गर्नुहोस्।',
  'fileDetail.preview.error.notFound': 'यो संलग्नक फेला परेन।',
  'fileDetail.preview.error.forbidden': 'यो संलग्नक हेर्ने अनुमति तपाईंसँग छैन।',
  'fileDetail.preview.error.network': 'हाम्रोतर्फ केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'fileDetail.preview.error.generic': 'यो संलग्नक पूर्वावलोकन गर्न सकिएन।',
  'fileDetail.actionsTitle': 'कारबाहीहरू',
  'fileDetail.actionForward': 'पठाउनुहोस्',
  'fileDetail.actionApprove': 'स्वीकृत गर्नुहोस्',
  'fileDetail.actionReject': 'अस्वीकृत गर्नुहोस्',
  'fileDetail.actionComment': 'टिप्पणी',
  'fileDetail.notHolder':
    'यो फाइल हाल जिम्मा लिने व्यक्तिको कारबाहीको पर्खाइमा छ। तपाईं टिप्पणी थप्न सक्नुहुन्छ, तर पठाउन, स्वीकृत वा अस्वीकृत गर्न सक्नुहुन्न।',
  'fileDetail.auditNotice': 'यो पहुँच रेकर्ड गरिएको छ, किनभने यो फाइल आन्तरिक भन्दा माथिको गोपनीयतामा छ।',
  'fileDetail.error.notFound.title': 'यो फाइल फेला परेन',
  'fileDetail.error.notFound.body': 'यो सारिएको हुन सक्छ, वा लिङ्क गलत हुन सक्छ।',
  'fileDetail.error.forbiddenRole.title': 'तपाईंलाई यो हेर्ने अनुमति छैन',
  'fileDetail.error.forbiddenRole.body':
    'तपाईंको भूमिकामा फाइल पहुँच छैन। यदि यो गलत हो भने आफ्नो शाखा प्रशासकलाई सम्पर्क गर्नुहोस्।',
  'fileDetail.error.forbiddenScope.title': 'तपाईंको दायरा बाहिर',
  'fileDetail.error.forbiddenScope.body': 'यो फाइल तपाईंलाई तोकिएको दायरा भन्दा बाहिरको संगठनात्मक एकाइको हो।',
  'fileDetail.error.forbiddenOwnership.title': 'तपाईं यस फाइलको हालको जिम्मेवार व्यक्ति होइन',
  'fileDetail.error.forbiddenOwnership.body':
    'यो फाइल अवस्थित छ र तपाईंको दायरामा छ, तर यसरी हेर्न वा कारबाही गर्न हालको जिम्मेवार व्यक्तिले मात्र सक्छ।',
  'fileDetail.error.forbiddenClearance.title': 'स्वीकृति तह आवश्यक',
  'fileDetail.error.forbiddenClearance.body': 'यो फाइलको गोपनीयता तह तपाईंको स्वीकृति भन्दा माथि छ।',
  'fileDetail.error.generic.title': 'हामी यो फाइल लोड गर्न सकेनौं',
  'fileDetail.error.generic.body': 'हाम्रोतर्फ केही समस्या भयो।',
  'fileDetail.movementsError.title': 'गतिविधि इतिहास लोड गर्न सकिएन',
  'movement.title.forward': 'यो फाइल पठाउनुहोस्',
  'movement.title.approve': 'यो फाइल स्वीकृत गर्नुहोस्',
  'movement.title.reject': 'यो फाइल अस्वीकृत गर्नुहोस्',
  'movement.title.comment': 'टिप्पणी थप्नुहोस्',
  'movement.description.forward': 'यो फाइल अब कहाँ जाने छान्नुहोस्। मान्य पछिल्ला जिम्मेवार व्यक्तिहरू मात्र सूचीबद्ध छन्।',
  'movement.description.approve': 'यो फाइल स्वीकृत चिन्हित हुनेछ। यसलाई पछि पनि पठाउन वा सम्पन्न गर्न सकिन्छ।',
  'movement.description.reject':
    'यो फाइल अस्वीकृत चिन्हित हुनेछ र निवेदकलाई सूचित गरिनेछ। यो स्क्रिनबाट फिर्ता गर्न सकिँदैन।',
  'movement.description.comment': 'यस फाइलको इतिहासमा टिप्पणी थप्नुहोस्। यसले स्थिति परिवर्तन गर्दैन।',
  'movement.forwardTarget.label': 'यहाँ पठाउनुहोस्',
  'movement.forwardTarget.placeholder': 'नाम वा शाखाबाट खोज्नुहोस्',
  'movement.forwardTarget.helper':
    'यो सर्भरको आफ्नै मान्य पछिल्ला जिम्मेवार व्यक्तिहरूको सूची हो — बिदामा रहेका वा हालको खटान नभएका अधिकारीहरू समावेश गरिएका छैनन्।',
  'movement.forwardTarget.empty': 'यस फाइलका लागि अहिले कुनै मान्य पछिल्लो जिम्मेवार व्यक्ति उपलब्ध छैन।',
  'movement.forwardTarget.onLeave': ' (बिदामा)',
  'movement.remarks.label': 'टिप्पणी',
  'movement.remarks.requiredHelper': 'आवश्यक — कारण नबताई अस्वीकृति रेकर्ड गर्न सकिँदैन।',
  'movement.remarks.optionalHelper': 'वैकल्पिक, तर अर्को जिम्मेवार व्यक्तिका लागि सिफारिस गरिन्छ।',
  'movement.remarks.required': 'फाइल अस्वीकृत गर्न टिप्पणी आवश्यक छ।',
  'movement.submit': 'पेश गर्नुहोस्',
  'movement.submitting': 'पेश हुँदैछ',
  'movement.cancel': 'रद्द गर्नुहोस्',
  'movement.error.conflict.title': 'तपाईंले यो खोलेपछि यो फाइल परिवर्तन भएको छ',
  'movement.error.conflict.body':
    'हालको स्थिति: {status}। हालको जिम्मेवार व्यक्ति: {holder}। पुनः प्रयास गर्नु अघि फाइल फेरि जाँच्नुहोस् — केही पेश गरिएको छैन।',
  'movement.error.conflict.holderOther': 'अर्को कोही',
  'movement.error.staleReload': 'यो फाइल पुनः लोड गर्नुहोस्',
  'movement.error.ownership.title': 'तपाईं अब यस फाइलमा कारबाही गर्न सक्नुहुन्न',
  'movement.error.ownership.body': 'यो पेश हुनु अघि यसको जिम्मेवार व्यक्ति परिवर्तन भयो। केही रेकर्ड गरिएको छैन।',
  'movement.error.state.title': 'अहिले यो कारबाही सम्भव छैन',
  'movement.error.state.body': 'यस फाइलको हालको स्थितिले यो कारबाहीलाई अनुमति दिँदैन। केही रेकर्ड गरिएको छैन।',
  'movement.error.target.title': 'त्यो अधिकारी अब मान्य लक्ष्य होइन',
  'movement.error.target.body': 'सूचीबाट अर्को अधिकारी छान्नुहोस् — केही रेकर्ड गरिएको छैन।',
  'movement.error.network': 'हाम्रोतर्फ केही समस्या भयो। केही रेकर्ड गरिएको छैन — पुनः प्रयास गर्नुहोस्।',
  'movement.error.generic': 'यो कारबाही पूरा गर्न सकिएन। फाइलको स्थिति परिवर्तन भएको छैन।',
  'movement.success': 'रेकर्ड भयो — फाइलको स्थिति र इतिहासले अब यो कारबाही देखाउँछ।',
  'myTasks.title': 'मेरा कार्यहरू',
  'myTasks.statTotal': 'जम्मा जिम्मा',
  'myTasks.statOverdue': 'म्याद नाघेको',
  'myTasks.filterOverdueOnly': 'म्याद नाघेका मात्र',
  'myTasks.caption': 'तपाईंलाई जिम्मा दिइएका फाइलहरू',
  'myTasks.column.trackingCode': 'ट्र्याकिङ कोड',
  'myTasks.column.subject': 'विषय',
  'myTasks.column.status': 'स्थिति',
  'myTasks.column.dueDate': 'म्याद',
  'myTasks.empty.title': 'अहिले तपाईंलाई कुनै फाइल जिम्मा दिइएको छैन',
  'myTasks.empty.body': 'तपाईंलाई फाइल पठाइएपछि यो यहाँ देखिनेछ।',
  'myTasks.noDueDate': '—',
  'notFound.title': 'पृष्ठ फेला परेन',
  'notFound.body': 'तपाईंले खोज्नुभएको पृष्ठ अवस्थित छैन, वा यसतर्फ औंल्याउने मेनु वस्तु अझै छैन।',
  'notFound.action': 'ड्यासबोर्डमा फर्कनुहोस्',
  'errorBoundary.title': 'केही गडबड भयो',
  'errorBoundary.body': 'यो स्क्रिनमा अप्रत्याशित त्रुटि आयो। पुनः लोड गर्दा प्रायः समाधान हुन्छ।',
  'errorBoundary.reload': 'पृष्ठ पुनः लोड गर्नुहोस्',

  // ---- I11 सूचना केन्द्र -----------------------------------------------------------
  'notifications.markAllRead': 'सबै पढिएको चिन्ह लगाउनुहोस्',
  'notifications.markingAll': 'चिन्ह लगाउँदै…',
  'notifications.title': 'सूचनाहरू',
  'notifications.deepLinkHint': 'सम्बन्धित फाइल खोल्छ',

  // ---- I7 नयाँ फाइल दर्ता -----------------------------------------------------------
  'fileIntake.title': 'नयाँ फाइल दर्ता गर्नुहोस्',
  'fileIntake.subtitle': 'ट्र्याकिङ कोड र QR रसिद एउटै कारोबारमा उत्पन्न हुन्छन् — समीक्षा चरणमा पुष्टि नगरेसम्म केही सिर्जना हुँदैन।',
  'fileIntake.step.applicant': 'निवेदकको विवरण',
  'fileIntake.step.file': 'फाइल विवरण',
  'fileIntake.step.review': 'समीक्षा',
  'fileIntake.applicant.fullName': 'निवेदकको पूरा नाम',
  'fileIntake.applicant.phone': 'फोन नम्बर',
  'fileIntake.applicant.phone.helper': 'नागरिकले यो नम्बरको अन्तिम ४ अंक प्रयोग गरेर अनलाइन आफ्नो फाइलको स्थिति जाँच्नेछन्।',
  'fileIntake.applicant.phone.error': 'मान्य नेपाल मोबाइल नम्बर लेख्नुहोस् (जस्तै 98XXXXXXXX वा +97798XXXXXXXX)।',
  'fileIntake.applicant.email': 'इमेल (वैकल्पिक)',
  'fileIntake.applicant.email.error': 'मान्य इमेल ठेगाना लेख्नुहोस्।',
  'fileIntake.applicant.address': 'ठेगाना (वैकल्पिक)',
  'fileIntake.applicant.required': 'यो फिल्ड आवश्यक छ।',
  'fileIntake.file.subject': 'विषय',
  'fileIntake.file.subject.helper': 'यो फाइल केको बारेमा हो — कम्तीमा ३ अक्षर।',
  'fileIntake.file.subject.error': 'विषय ३ देखि ५०० अक्षरको बीचमा हुनुपर्छ।',
  'fileIntake.file.section': 'शाखा',
  'fileIntake.file.section.placeholder': 'यो फाइल सम्बन्धित शाखा छान्नुहोस्',
  'fileIntake.file.section.helper': 'यस संस्करणमा `/reference/org-units` एन्डपोइन्ट उपलब्ध छैन — यो सूची तपाईंको आफ्नै फाइल रजिस्टरमा देखिएका शाखाहरूबाट बनाइएको हो। तपाईंको शाखा सूचीमा नभए, त्यस शाखामा पहिले नै कम्तीमा एउटा फाइल हुनुपर्छ, वा प्रशासन/मानव संसाधनलाई सम्पर्क गर्नुहोस्।',
  'fileIntake.file.section.empty': 'कुनै शाखा फेला परेन। पहिले फाइल रजिस्टर खोल्नुहोस्, वा प्रशासन/मानव संसाधनलाई सम्पर्क गर्नुहोस् — शाखा बिना फाइल दर्ता गर्न सकिँदैन।',
  'fileIntake.file.section.required': 'शाखा छान्नुहोस्।',
  'fileIntake.darta.title': 'दर्ता-चलानी सन्दर्भ (वैकल्पिक)',
  'fileIntake.darta.helper': 'वैकल्पिक — अहिले उपलब्ध नभए फाइलको विवरण पृष्ठबाट पछि पनि लिङ्क गर्न सकिन्छ।',
  'fileIntake.darta.dartaNumber': 'दर्ता नम्बर',
  'fileIntake.darta.chalaniNumber': 'चलानी नम्बर',
  'fileIntake.darta.registerDate': 'दर्ता मिति',
  'fileIntake.nav.back': 'पछाडि',
  'fileIntake.nav.next': 'अर्को',
  'fileIntake.nav.submit': 'फाइल दर्ता गर्नुहोस्',
  'fileIntake.nav.submitting': 'दर्ता गर्दै…',
  'fileIntake.review.title': 'पेश गर्नु अघि समीक्षा गर्नुहोस्',
  'fileIntake.review.body': 'पेश गरेपछि ट्र्याकिङ कोड र QR रसिद उत्पन्न हुन्छ र यो स्क्रिनबाट फिर्ता लिन सकिँदैन। तलका सबै फिल्ड जाँच्नुहोस्।',
  'fileIntake.review.applicant': 'निवेदक',
  'fileIntake.review.file': 'फाइल',
  'fileIntake.review.darta': 'दर्ता-चलानी',
  'fileIntake.review.dartaNone': 'उपलब्ध गराइएको छैन — पछि लिङ्क गर्न सकिन्छ।',
  'fileIntake.error.title': 'फाइल दर्ता गर्न सकिएन',
  'fileIntake.error.body': 'केही सिर्जना भएन। हाइलाइट गरिएका फिल्डहरू जाँच्नुहोस्, वा फेरि प्रयास गर्नुहोस्।',
  'fileIntake.error.network': 'हाम्रोतर्फ केही समस्या भयो। केही सिर्जना भएन — फेरि प्रयास गर्नुहोस्।',
  'fileIntake.error.sectionScope': 'त्यो शाखा तपाईंको जिम्मेवारी बाहिर छ। तपाईं जिम्मेवार भएको शाखा छान्नुहोस्।',
  'fileIntake.success.title': 'फाइल दर्ता भयो',
  'fileIntake.success.body': 'ट्र्याकिङ कोड {code} उत्पन्न भयो। नागरिकलाई यो कोड र QR दिनुहोस् — अनलाइन स्थिति जाँच्न उनीहरूलाई यो चाहिन्छ।',
  'fileIntake.success.trackingCode': 'ट्र्याकिङ कोड',
  'fileIntake.success.qrToken': 'QR टोकन',
  'fileIntake.success.qrLabel': 'ट्र्याकिङ कोड {code} को लागि QR — सार्वजनिक स्थिति पृष्ठ खोल्न स्क्यान गर्नुहोस्',
  'fileIntake.success.qrError': 'QR कोड लोड गर्न सकिएन।',
  'fileIntake.success.print': 'रसिद प्रिन्ट गर्नुहोस्',
  'fileIntake.success.viewFile': 'यो फाइल खोल्नुहोस्',
  'fileIntake.newFile': 'नयाँ फाइल',
  'fileIntake.forbidden.title': 'तपाईं फाइल दर्ता गर्न सक्नुहुन्न',
  'fileIntake.forbidden.body': 'तपाईंको भूमिकामा फाइल दर्ता समावेश छैन। यदि यो गलत हो भने प्रशासन/मानव संसाधनलाई सम्पर्क गर्नुहोस्।',

  // ---- फाइल विवरण — थप कारबाहीहरू --------------------------------------------------
  'fileDetail.recordActionsTitle': 'रेकर्ड',
  'fileDetail.edit.action': 'सम्पादन',
  'fileDetail.edit.title': 'फाइल सम्पादन गर्नुहोस्',
  'fileDetail.edit.subject': 'विषय',
  'fileDetail.edit.dueDate': 'म्याद मिति',
  'fileDetail.edit.submit': 'परिवर्तन सुरक्षित गर्नुहोस्',
  'fileDetail.edit.submitting': 'सुरक्षित गर्दै…',
  'fileDetail.edit.success': 'फाइल अद्यावधिक भयो।',
  'fileDetail.darta.edit': 'सन्दर्भ सम्पादन गर्नुहोस्',
  'fileDetail.darta.editTitle': 'दर्ता-चलानी सन्दर्भ',
  'fileDetail.darta.success': 'दर्ता-चलानी सन्दर्भ सुरक्षित भयो।',
  'fileDetail.classify.action': 'वर्गीकरण परिवर्तन गर्नुहोस्',
  'fileDetail.classify.title': 'गोपनीयता वर्गीकरण परिवर्तन गर्नुहोस्',
  'fileDetail.classify.level': 'नयाँ गोपनीयता स्तर',
  'fileDetail.classify.reason': 'यो परिवर्तनको कारण',
  'fileDetail.classify.reason.required': 'वर्गीकरण परिवर्तनको लागि कारण आवश्यक छ।',
  'fileDetail.classify.submit': 'वर्गीकरण परिवर्तन गर्नुहोस्',
  'fileDetail.classify.success': 'वर्गीकरण अद्यावधिक भयो।',
  'fileDetail.qr.action': 'QR / रसिद हेर्नुहोस्',
  'fileDetail.qr.title': 'नागरिक रसिद',
  'fileDetail.qr.body': 'नागरिकलाई यो ट्र्याकिङ कोड र QR टोकन दिनुहोस् — यो सार्वजनिक ट्र्याकिङ पृष्ठमा प्रयोग हुन्छ।',
  'fileDetail.qr.trackingCode': 'ट्र्याकिङ कोड',
  'fileDetail.qr.token': 'QR टोकन',
  'fileDetail.qr.fragmentPath': 'फ्र्यागमेन्ट पथ (कहिल्यै सर्भरमा पठाइँदैन)',
  'fileDetail.qr.error': 'QR रसिद लोड गर्न सकिएन।',
  'fileDetail.reopen.action': 'पुनः खोल्नुहोस्',
  'fileDetail.reopen.title': 'यो फाइल पुनः खोल्नुहोस्',
  'fileDetail.reopen.body': 'अस्वीकृत भएको फाइलबाट मात्र सम्भव। समयरेखामा अस्वीकृत → पुनः खोलियो देखिनेछ।',
  'fileDetail.reopen.remarks': 'कैफियत',
  'fileDetail.reopen.remarks.required': 'यो फाइल किन पुनः खोलिँदैछ भनेर उल्लेख गर्नुहोस्।',
  'fileDetail.reopen.submit': 'फाइल पुनः खोल्नुहोस्',
  'fileDetail.reopen.success': 'फाइल पुनः खोलियो।',
  'fileDetail.reassign.action': 'पुनः जिम्मा दिनुहोस्',
  'fileDetail.reassign.title': 'यो फाइल पुनः जिम्मा दिनुहोस्',
  'fileDetail.reassign.body': 'हालको जिम्मेवार व्यक्तिले कारबाही गर्न नसक्दाका लागि — तपाईं हालको जिम्मेवार हुनुपर्दैन।',
  'fileDetail.reassign.target': 'यसलाई जिम्मा दिनुहोस्',
  'fileDetail.reassign.reason': 'कारण',
  'fileDetail.reassign.remarks': 'कैफियत',
  'fileDetail.reassign.remarks.required': 'यो पुनः जिम्माको कारण उल्लेख गर्नुहोस्।',
  'fileDetail.reassign.submit': 'पुनः जिम्मा दिनुहोस्',
  'fileDetail.reassign.success': 'फाइल पुनः जिम्मा दिइयो।',
  'fileDetail.upload.action': 'संलग्नक थप्नुहोस्',
  'fileDetail.upload.success': 'संलग्नक अपलोड भयो।',
  'fileDetail.upload.confidentiality': 'गोपनीयता स्तर (वैकल्पिक — फाइलको आफ्नै स्तरमा डिफल्ट हुन्छ)',
  'fileDetail.archive.action': 'सङ्ग्रह गर्नुहोस्',
  'fileDetail.archive.confirm': 'यो संलग्नक सङ्ग्रह गर्ने? यो अब सक्रिय सूचीमा देखिने छैन (A-006: फिर्ता लिइएको, कहिल्यै मेटाइएको होइन)।',
  'fileDetail.archive.success': 'संलग्नक सङ्ग्रह गरियो।',
  'fileDetail.archive.error': 'यो संलग्नक सङ्ग्रह गर्न सकिएन।',
  'fileDetail.attachmentPendingScanRetryable': 'स्क्यान अझै जारी छ — पूरा भएपछि यो स्वतः अद्यावधिक हुनेछ।',
  'fileDetail.conflict.title': 'यो फाइल पढिएदेखि परिवर्तन भएको छ',
  'fileDetail.conflict.body': 'फेरि प्रयास गर्नु अघि फाइल पुनः लोड गर्नुहोस् — केही पेश गरिएको छैन।',
  'fileDetail.conflict.reload': 'यो फाइल पुनः लोड गर्नुहोस्',

  'common.cancel': 'रद्द गर्नुहोस्',
  'common.dismiss': 'हटाउनुहोस्',

  'shell.nav.appointments': 'भेटघाट इनबक्स',
  'shell.nav.entranceVerification': 'प्रवेश प्रमाणीकरण',
  'shell.nav.myAvailability': 'मेरो उपलब्धता',
  'shell.nav.availabilityAdmin': 'कर्मचारी उपलब्धता',
  'shell.nav.notifications': 'सूचनाहरू',

  // साझा 403/500 अवस्थाहरू (SCREEN_SPECS.md I24)
  'forbidden.back': 'ड्यासबोर्डमा फर्कनुहोस्',
  'forbidden.notFound.title': 'यो फेला परेन',
  'forbidden.notFound.body': 'यो सारिएको हुन सक्छ, वा लिङ्क गलत हुन सक्छ।',
  'forbidden.role.title': 'तपाईंलाई यो हेर्ने अनुमति छैन',
  'forbidden.role.body': 'तपाईंको भूमिकाले यो क्षमता समावेश गर्दैन। यदि यो गलत लाग्छ भने आफ्नो शाखा प्रशासकलाई सम्पर्क गर्नुहोस्।',
  'forbidden.scope.title': 'तपाईंलाई यो हेर्ने अनुमति छैन',
  'forbidden.scope.body': 'यो तपाईंको खातालाई तोकिएको दायराभन्दा बाहिर छ। यदि यो गलत लाग्छ भने आफ्नो शाखा प्रशासकलाई सम्पर्क गर्नुहोस्।',
  'forbidden.ownership.title': 'तपाईंलाई यो हेर्ने अनुमति छैन',
  'forbidden.ownership.body': 'यो रेकर्ड अरू कसैको कार्यसूचीको हो। यदि यो गलत लाग्छ भने आफ्नो शाखा प्रशासकलाई सम्पर्क गर्नुहोस्।',
  'forbidden.clearance.title': 'तपाईंलाई यो हेर्ने अनुमति छैन',
  'forbidden.clearance.body': 'यस रेकर्डको गोपनीयता स्तर तपाईंको स्वीकृति स्तरभन्दा माथि छ। यदि यो गलत लाग्छ भने आफ्नो शाखा प्रशासकलाई सम्पर्क गर्नुहोस्।',
  'forbidden.generic.title': 'तपाईंलाई यो हेर्ने अनुमति छैन',
  'forbidden.generic.body': 'यदि यो गलत लाग्छ भने आफ्नो शाखा प्रशासकलाई सम्पर्क गर्नुहोस्।',

  'serverError.title': 'हाम्रोतर्फ केही समस्या भयो',
  'serverError.body': 'कृपया केही क्षणपछि पुनः प्रयास गर्नुहोस्।',
  'serverError.correlationId': 'सहयोगका लागि सन्दर्भ: {id}',

  'sessionExpired.title': 'तपाईंको सत्र समाप्त भयो',
  'sessionExpired.body': 'तपाईंको सुरक्षाका लागि, निष्क्रिय समयपछि तपाईंलाई लगआउट गरियो। जहाँबाट छोड्नुभएको थियो त्यहीँबाट जारी राख्न पुनः लगइन गर्नुहोस्।',
  'sessionExpired.action': 'पुनः लगइन गर्नुहोस्',

  // I4 — पासवर्ड रिसेट
  'passwordReset.request.title': 'आफ्नो पासवर्ड रिसेट गर्नुहोस्',
  'passwordReset.request.body': 'आफ्नो मन्त्रालय कोड र प्रयोगकर्ता नाम प्रविष्ट गर्नुहोस् — खाता अवस्थित भएमा हामी रिसेट लिङ्क पठाउनेछौं।',
  'passwordReset.request.submit': 'रिसेट लिङ्क पठाउनुहोस्',
  'passwordReset.request.submitting': 'पठाउँदै',
  'passwordReset.request.success.title': 'यदि त्यो खाता अवस्थित छ भने, रिसेट लिङ्क पठाइएको छ',
  'passwordReset.request.success.body': 'यदि तपाईंले प्राप्त गर्नुभएन भने आफ्नो एडमिन/एचआर सम्पर्कलाई जाँच्नुहोस् — लिङ्क सीमित समयपछि म्याद सकिन्छ।',
  'passwordReset.request.error.network': 'हाम्रोतर्फ केही समस्या भयो। कृपया आफ्नो इन्टरनेट जाँचेर पुनः प्रयास गर्नुहोस्।',
  'passwordReset.request.error.generic': 'केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'passwordReset.complete.title': 'नयाँ पासवर्ड छान्नुहोस्',
  'passwordReset.complete.submit': 'नयाँ पासवर्ड सेट गर्नुहोस्',
  'passwordReset.complete.submitting': 'सुरक्षित गर्दै',
  'passwordReset.complete.success.title': 'तपाईंको पासवर्ड परिवर्तन भयो',
  'passwordReset.complete.success.body': 'तपाईंको सुरक्षाका लागि {count} अन्य सत्र(हरू) बाट लगआउट गरियो। नयाँ पासवर्डले लगइन गर्नुहोस्।',
  'passwordReset.complete.expired.title': 'यो रिसेट लिङ्कको म्याद सकियो',
  'passwordReset.complete.expired.body': 'तल नयाँ अनुरोध गर्नुहोस् — अघिल्लो लिङ्क अब प्रयोग गर्न सकिँदैन।',
  'passwordReset.complete.noToken.title': 'यो लिङ्कमा रिसेट टोकन छैन',
  'passwordReset.complete.noToken.body': 'आफ्नो रिसेट इमेलबाट लिङ्क फेरि खोल्नुहोस्, वा तल नयाँ अनुरोध गर्नुहोस्।',
  'passwordReset.complete.error.network': 'हाम्रोतर्फ केही समस्या भयो। कृपया आफ्नो इन्टरनेट जाँचेर पुनः प्रयास गर्नुहोस्।',
  'passwordReset.complete.error.generic': 'केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'passwordReset.complete.requestNewLink': 'नयाँ रिसेट लिङ्क अनुरोध गर्नुहोस्',

  // I11 — सूचना केन्द्र (पूर्ण पृष्ठ)
  'notifications.centre.title': 'सूचनाहरू',
  'notifications.centre.description': 'तपाईंको खातामा पठाइएका सबै सूचना — जिम्मेवारी, SLA चेतावनी, भेटघाट अद्यावधिक, र उल्लेखहरू।',
  'notifications.centre.unreadOnly': 'नपढेका मात्र देखाउनुहोस्',
  'notifications.centre.empty.title': 'अहिलेसम्म कुनै सूचना छैन',
  'notifications.centre.empty.body': 'जिम्मेवारी, SLA चेतावनी, भेटघाट अद्यावधिक र उल्लेखहरू यहाँ हुँदा-हुँदै देखिनेछन्।',
  'notifications.centre.empty.unread.title': 'नपढेको कुनै सूचना छैन',
  'notifications.centre.empty.unread.body': 'तपाईं पूर्ण रूपमा अद्यावधिक हुनुहुन्छ। पूरा इतिहास हेर्न "नपढेका मात्र" बन्द गर्नुहोस्।',
  'notifications.centre.error.title': 'तपाईंका सूचनाहरू लोड गर्न सकिएन',
  'notifications.centre.error.body': 'कृपया पुनः प्रयास गर्नुहोस्।',
  'notifications.centre.loadMore': 'थप लोड गर्नुहोस्',

  // I12 — मेरो उपलब्धता
  'availability.my.title': 'मेरो उपलब्धता',
  'availability.my.description': 'सार्वजनिक निर्देशिकामा तपाईंलाई खोज्दा नागरिकहरूले देख्ने कुरा। चरण १ म्यानुअल हो — अझै कुनै स्वचालित फिड छैन।',
  'availability.my.current': 'हालको स्थिति:',
  'availability.my.quickToggle': 'अहिले उपलब्ध छु',
  'availability.my.statusLabel': 'स्थिति',
  'availability.my.citizenSees': 'तपाईंलाई खोज्ने नागरिकले देख्छ:',
  'availability.my.noteLabel': 'आन्तरिक टिप्पणी (सार्वजनिकलाई देखिँदैन)',
  'availability.my.noteHelper': 'कर्मचारीलाई मात्र देखिन्छ — जस्तै "दिउँसो २ बजे फर्किन्छु।" सार्वजनिक निर्देशिकामा कहिल्यै देखिँदैन।',
  'availability.my.save': 'सुरक्षित गर्नुहोस्',
  'availability.my.saving': 'सुरक्षित गर्दै',
  'availability.my.saved': 'सुरक्षित भयो।',
  'availability.my.saveError.title': 'तपाईंको स्थिति अद्यावधिक गर्न सकिएन',
  'availability.my.saveError.body': 'यो सार्वजनिक रूपमा अझै "{status}" देखाउँछ।',
  'availability.my.loadError.title': 'तपाईंको उपलब्धता लोड गर्न सकिएन',
  'availability.my.loadError.body': 'कृपया पुनः प्रयास गर्नुहोस्।',

  // I13 — उपलब्धता प्रशासन
  'availability.admin.title': 'कर्मचारी उपलब्धता',
  'availability.admin.description': 'सार्वजनिक अधिकृत निर्देशिकामा हाल देखिने हरेक कर्मचारी, र तिनीहरूको स्थिति।',
  'availability.admin.caption': 'कर्मचारी उपलब्धता',
  'availability.admin.column.name': 'नाम',
  'availability.admin.column.designation': 'पद',
  'availability.admin.column.orgUnit': 'शाखा',
  'availability.admin.column.status': 'स्थिति',
  'availability.admin.column.override': 'स्थिति सेट गर्नुहोस्',
  'availability.admin.empty.title': 'यस शाखामा अझै कुनै कर्मचारी रेकर्ड छैन',
  'availability.admin.empty.body': 'खाता सेटअप भएपछि कर्मचारी उपलब्धता यहाँ देखिनेछ।',
  'availability.admin.error.conflict': 'पृष्ठ लोड भएदेखि यो व्यक्तिको स्थिति परिवर्तन भयो — पुनः लोड गर्दै।',
  'availability.admin.error.generic': 'यो स्थिति अद्यावधिक गर्न सकिएन। कृपया पुनः प्रयास गर्नुहोस्।',

  // I14 — भेटघाट इनबक्स
  'appointments.inbox.title': 'भेटघाट इनबक्स',
  'appointments.inbox.description': 'सार्वजनिक पोर्टलमार्फत गरिएका भेटघाट अनुरोधहरू — प्रत्येकलाई स्वीकार, पुनर्निर्धारण वा अस्वीकार गर्नुहोस्।',
  'appointments.inbox.caption': 'भेटघाट अनुरोधहरू',
  'appointments.statPending': 'विचाराधीन अनुरोधहरू',
  'appointments.tab.label': 'स्थिति अनुसार फिल्टर गर्नुहोस्',
  'appointments.tab.all': 'सबै',
  'appointments.tab.pending': 'विचाराधीन',
  'appointments.tab.accepted': 'स्वीकृत',
  'appointments.tab.rescheduled': 'पुनर्निर्धारित',
  'appointments.tab.declined': 'अस्वीकृत',
  'appointments.tab.completed': 'सम्पन्न',
  'appointments.tab.cancelled': 'रद्द',
  'appointments.column.when': 'कहिले',
  'appointments.column.status': 'स्थिति',
  'appointments.column.visitor': 'भेट्न आउने',
  'appointments.column.purpose': 'प्रयोजन',
  'appointments.column.myAvailability': 'मेरो उपलब्धता',
  'appointments.column.actions': 'कार्यहरू',
  'appointments.action.accept': 'स्वीकार गर्नुहोस्',
  'appointments.action.reschedule': 'पुनर्निर्धारण गर्नुहोस्',
  'appointments.action.decline': 'अस्वीकार गर्नुहोस्',
  'appointments.action.cancel': 'रद्द गर्नुहोस्',
  'appointments.empty.pending.title': 'कुनै विचाराधीन भेटघाट अनुरोध छैन',
  'appointments.empty.pending.body': 'तपाईं अद्यावधिक हुनुहुन्छ — नयाँ अनुरोधहरू यहाँ देखिनेछन्।',
  'appointments.empty.filtered.title': 'यो फिल्टरसँग कुनै भेटघाट मिल्दैन',
  'appointments.empty.filtered.body': 'अर्को स्थिति ट्याब प्रयास गर्नुहोस्।',
  'appointments.forbidden.title': 'तपाईंलाई यो हेर्ने अनुमति छैन',
  'appointments.forbidden.body': 'यदि यो गलत लाग्छ भने आफ्नो शाखा प्रशासकलाई सम्पर्क गर्नुहोस्।',
  'appointments.submitting': 'सुरक्षित गर्दै',
  'appointments.requestedFor': '{visitor} ले यसका लागि अनुरोध गर्नुभयो: {purpose}',
  'appointments.accept.title': 'यो भेटघाट स्वीकार गर्नुहोस्',
  'appointments.accept.description': 'मिति र समय पुष्टि गर्नुहोस् — पूर्वनिर्धारित रूपमा, नागरिकले अनुरोध गरेको समय।',
  'appointments.accept.submit': 'भेटघाट स्वीकार गर्नुहोस्',
  'appointments.reschedule.title': 'यो भेटघाट पुनर्निर्धारण गर्नुहोस्',
  'appointments.reschedule.description': 'यो भेटघाटलाई नयाँ मिति र समयमा सार्नुहोस्। नागरिकले अद्यावधिक तालिका देख्नेछन्।',
  'appointments.reschedule.submit': 'नयाँ समय सुरक्षित गर्नुहोस्',
  'appointments.field.date': 'मिति',
  'appointments.field.time': 'समय',
  'appointments.field.time.invalid': 'समय HH:MM को रूपमा प्रविष्ट गर्नुहोस्, जस्तै 14:30।',
  'appointments.field.rescheduleReason': 'पुनर्निर्धारणको कारण (वैकल्पिक)',
  'appointments.field.rescheduleReason.helper': 'आन्तरिक मात्र — नागरिकलाई देखिँदैन।',
  'appointments.decline.title': 'यो भेटघाट अस्वीकार गर्नुहोस्',
  'appointments.decline.description': 'तपाईंले तल छान्नुभएको कारणको द्विभाषी, नागरिक-सुरक्षित संस्करण नागरिकलाई भनिनेछ — तपाईंको आफ्नै शब्द जस्ताको तस्तै कहिल्यै होइन।',
  'appointments.decline.submit': 'भेटघाट अस्वीकार गर्नुहोस्',
  'appointments.decline.reasonCode': 'कारण वर्ग',
  'appointments.decline.reasonCode.placeholder': 'कारण छान्नुहोस्',
  'appointments.decline.citizenSees': 'नागरिकले देख्नेछन्:',
  'appointments.decline.reason': 'आन्तरिक टिप्पणी (नागरिकलाई देखिँदैन)',
  'appointments.decline.reason.required': 'कारण प्रविष्ट गर्नुहोस् — बिना कारण अस्वीकार दर्ता गर्न सकिँदैन।',
  'appointments.decline.reason.helper': 'तपाईंको आफ्नै अभिलेख र पछि यो हेर्ने सहकर्मीहरूका लागि।',
  'appointments.decline.error.slotNotTaken.title': 'त्यो समय वास्तवमा लिइएको छैन',
  'appointments.decline.error.slotNotTaken.body': 'त्यो समयमा कुनै स्वीकृत भेटघाट छैन, त्यसैले यसलाई "समय लिइएको" भनेर दर्ता गर्न सकिँदैन — वास्तवमा लागू हुने कारण छान्नुहोस्।',
  'appointments.cancel.title': 'यो भेटघाट रद्द गर्नुहोस्',
  'appointments.cancel.description': 'कार्यालयले पहिले नै स्वीकृत गरेको भेटघाट अब राख्न नसक्दा यो प्रयोग गर्नुहोस्।',
  'appointments.cancel.submit': 'भेटघाट रद्द गर्नुहोस्',
  'appointments.cancel.reason': 'कारण (वैकल्पिक)',
  'appointments.cancel.reason.helper': 'आन्तरिक मात्र।',
  'appointments.error.conflict.title': 'यो भेटघाट पढिएदेखि परिवर्तन भएको छ',
  'appointments.error.conflict.body': 'यसको हालको स्थिति अब "{status}" छ। फेरि प्रयास गर्नु अघि यसलाई पुनः लोड गर्नुहोस्।',
  'appointments.error.staleReload': 'यो भेटघाट पुनः लोड गर्नुहोस्',
  'appointments.error.slotTaken.title': 'त्यो समय पहिले नै लिइसकियो',
  'appointments.error.slotTaken.body': 'अर्को भेटघाटले पहिले नै त्यो ठ्याक्कै समय लिइसकेको छ। फरक समय छान्नुहोस्।',
  'appointments.error.state.title': 'यो भेटघाट अब परिवर्तन गर्न सकिँदैन',
  'appointments.error.state.body': 'यसको हालको स्थितिले अब यो कार्यलाई अनुमति दिँदैन।',
  'appointments.error.ownership.title': 'यो तपाईंलाई तोकिएको छैन',
  'appointments.error.ownership.body': 'यो अनुरोध जसका लागि बनाइएको थियो त्यही अधिकृतले मात्र यसमा कारबाही गर्न सक्छन्।',
  'appointments.error.network': 'हाम्रोतर्फ केही समस्या भयो। कृपया आफ्नो इन्टरनेट जाँचेर पुनः प्रयास गर्नुहोस्।',
  'appointments.error.generic': 'केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',

  // I15 — भेटघाट विवरण र प्रवेश प्रमाणीकरण
  'appointments.detail.title': 'भेटघाट',
  'appointments.detail.visitor': 'भेट्न आउने',
  'appointments.detail.when': 'कहिले',
  'appointments.detail.purpose': 'प्रयोजन',
  'appointments.detail.requestedOnly': '(अनुरोध गरिएको समय — अझै तालिका बनाइएको छैन)',
  'appointments.detail.myAvailability': 'त्यस समयमा मेरो उपलब्धता',
  'appointments.detail.declineReason': 'अस्वीकार कारण (आन्तरिक)',
  'appointments.detail.timeline': 'इतिहास',
  'appointments.detail.notFound.title': 'यो भेटघाट फेला परेन',
  'appointments.detail.notFound.body': 'यो हटाइएको हुन सक्छ, वा लिङ्क गलत हुन सक्छ।',
  'appointments.detail.error.title': 'यो भेटघाट लोड गर्न सकिएन',
  'appointments.detail.error.body': 'कृपया पुनः प्रयास गर्नुहोस्।',
  'appointments.timeline.actorStaff': 'कर्मचारी',
  'appointments.timeline.actorPublic': 'नागरिक (सार्वजनिक पोर्टल)',
  'appointments.timeline.actorSystem': 'प्रणाली',
  'appointments.verify.title': 'प्रवेश प्रमाणीकरण',
  'appointments.verify.description': 'आगमन पुष्टि गर्न भेट्न आउनेको सन्दर्भ नम्बर वा QR कोड स्क्यान गर्नुहोस् वा प्रविष्ट गर्नुहोस्।',
  'appointments.verify.codeLabel': 'सन्दर्भ नम्बर वा QR कोड',
  'appointments.verify.codeHelper': 'जस्तै APT-XXXXXXXXXX, भेट्न आउनेको पुष्टिकरणबाट।',
  'appointments.verify.codeInvalid': 'यो मान्य सन्दर्भ नम्बर वा QR कोड जस्तो देखिँदैन।',
  'appointments.verify.submit': 'प्रमाणित गर्नुहोस्',
  'appointments.verify.submitting': 'प्रमाणित गर्दै',
  'appointments.verify.scanWithCamera': 'क्यामेराले स्क्यान गर्नुहोस्',
  'appointments.verify.cameraPreviewLabel': 'क्यामेरा पूर्वावलोकन — भेट्न आउनेको QR कोडतिर देखाउनुहोस्',
  'appointments.verify.camera.unsupported': 'यो ब्राउजरमा क्यामेरा स्क्यानिङ समर्थित छैन — सन्दर्भ नम्बर प्रविष्ट गर्नुहोस्।',
  'appointments.verify.camera.error': 'क्यामेरा पहुँच गर्न सकिएन — सन्दर्भ नम्बर प्रविष्ट गर्नुहोस्।',
  'appointments.verify.error.invalidOrUsed': 'यो कोड मान्य छैन वा पहिले नै प्रयोग भइसकेको छ।',
  'appointments.verify.error.currentStatus': 'हालको स्थिति: {status}',
  'appointments.verify.error.format': 'यो मान्य सन्दर्भ नम्बर वा QR कोड जस्तो देखिँदैन।',
  'appointments.verify.error.network': 'हाम्रोतर्फ केही समस्या भयो। कृपया आफ्नो इन्टरनेट जाँचेर पुनः प्रयास गर्नुहोस्।',
  'appointments.verify.error.generic': 'केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'appointments.verify.markCompleted': 'सम्पन्न भएको चिन्ह लगाउनुहोस्',
  'appointments.verify.completing': 'सुरक्षित गर्दै',
  'appointments.verify.completeError': 'यसलाई सम्पन्न भनी चिन्ह लगाउन सकिएन। कृपया पुनः प्रयास गर्नुहोस्।',
  'appointments.verify.alreadyCompleted': 'यो भेटघाट पहिले नै सम्पन्न भनी चिन्ह लगाइसकिएको छ।',
  'appointments.verify.viewFullDetail': 'पूर्ण भेटघाट विवरण हेर्नुहोस्',

  'toast.movement.forward': 'फाइल पठाइयो।',
  'toast.movement.approve': 'फाइल स्वीकृत भयो।',
  'toast.movement.reject': 'फाइल अस्वीकृत भयो।',
  'toast.movement.comment': 'टिप्पणी थपियो।',
  'toast.file.edited': 'फाइल विवरण अद्यावधिक भयो।',
  'toast.file.darta': 'दर्ता/चलानी सन्दर्भ सुरक्षित भयो।',
  'toast.file.classified': 'गोपनीयता स्तर अद्यावधिक भयो।',
  'toast.file.reopened': 'फाइल पुनः खोलियो।',
  'toast.file.reassigned': 'फाइल पुनः जिम्मा दिइयो।',
  'toast.appointments.accepted': 'भेटघाट स्वीकृत भयो।',
  'toast.appointments.rescheduled': 'भेटघाट पुनः तालिका बनाइयो।',
  'toast.appointments.declined': 'भेटघाट अस्वीकार गरियो।',
  'toast.appointments.cancelled': 'भेटघाट रद्द गरियो।',
  'toast.appointments.verified': 'भ्रमण सम्पन्न भनी चिन्ह लगाइयो।',
  'toast.notifications.markedAll': 'सबै सूचनाहरू पढिएको चिन्ह लगाइयो।',

  // ---- Administration (I19-I22) ----
  'admin.nav.users': 'कर्मचारी निर्देशिका',
  'admin.nav.orgUnits': 'संगठन संरचना',
  'admin.nav.tenants': 'मन्त्रालयहरू (प्लेटफर्म)',
  'admin.nav.platformAudit': 'प्लेटफर्म लेखा परीक्षण लग',
  'admin.common.cancel': 'रद्द गर्नुहोस्',
  'admin.common.retry': 'पुनः प्रयास गर्नुहोस्',
  'admin.common.save': 'सुरक्षित गर्नुहोस्',
  'admin.common.saving': 'सुरक्षित हुँदैछ',
  'admin.error.checkFields': 'कृपया हाइलाइट गरिएका फिल्डहरू जाँच गर्नुहोस्।',
  'admin.error.generic': 'केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'admin.error.network': 'हाम्रोतर्फ केही समस्या भयो। कृपया आफ्नो इन्टरनेट जाँचेर पुनः प्रयास गर्नुहोस्।',
  'fieldError.required': 'यो फिल्ड आवश्यक छ।',
  'fieldError.invalidType': 'यो मान सही प्रकारको छैन।',
  'fieldError.invalidFormat': 'यो मान अपेक्षित ढाँचामा छैन।',
  'fieldError.tooShort': 'यो मान धेरै छोटो छ (न्यूनतम {min})।',
  'fieldError.tooLong': 'यो मान धेरै लामो छ (अधिकतम {max})।',
  'fieldError.outOfRange': 'यो मान अनुमति दिइएको सीमाभन्दा बाहिर छ।',
  'fieldError.notAllowedValue': 'यो मानको अनुमति छैन।',
  'fieldError.unknownField': 'यो फिल्ड चिन्न सकिएन।',
  'fieldError.generic': 'यो मान अस्वीकार गरियो।',
  'admin.grantRefusal.noGrantAuthority': 'तपाईंसँग हाल अरूलाई भूमिका प्रदान गर्न सक्ने कुनै भूमिका छैन।',
  'admin.grantRefusal.selfGrant': 'तपाईं आफैंलाई कुनै पनि भूमिका प्रदान गर्न सक्नुहुन्न — यो अनुदान अर्को प्रशासकले गर्नुपर्छ।',
  'admin.grantRefusal.roleAboveActor': 'यो भूमिकाले तपाईंसँग नभएका प्रशासनिक अनुमतिहरू बोक्छ — तपाईं आफूभन्दा माथिको भूमिका प्रदान गर्न सक्नुहुन्न।',
  'admin.grantRefusal.scopeAboveActor': 'तपाईंले छनौट गर्नुभएको दायरा तपाईंको आफ्नै प्रशासनिक दायराभन्दा फराकिलो छ — तपाईंसँग नभएको पहुँच सिर्जना गर्न सकिँदैन।',
  'admin.grantRefusal.scopeOutsideActor': 'तपाईंले छनौट गर्नुभएको संगठन इकाई तपाईंले प्रशासन गर्ने भागभन्दा बाहिर छ।',
  'admin.grantRefusal.clearanceAboveActor': 'तपाईंले छनौट गर्नुभएको गोपनीयता तह तपाईंको आफ्नैभन्दा माथि छ — तपाईंसँग नभएको तह प्रदान गर्न सकिँदैन।',
  'admin.grantRefusal.generic': 'यो अनुदान अस्वीकार गरियो।',
  'admin.users.title': 'कर्मचारी निर्देशिका',
  'admin.users.description': 'तपाईंको मन्त्रालयभरका कर्मचारी खाता, भूमिका र पहुँच व्यवस्थापन गर्नुहोस्।',
  'admin.users.newUser': 'नयाँ प्रयोगकर्ता',
  'admin.users.search.label': 'खोज्नुहोस्',
  'admin.users.search.placeholder': 'नाम वा प्रयोगकर्ता नाम',
  'admin.users.search.submit': 'खोज्नुहोस्',
  'admin.users.filter.status.label': 'स्थिति',
  'admin.users.filter.status.all': 'सबै स्थिति',
  'admin.users.filter.status.active': 'सक्रिय',
  'admin.users.filter.status.suspended': 'निलम्बित',
  'admin.users.filter.status.deactivated': 'निष्क्रिय पारिएको',
  'admin.users.column.name': 'नाम',
  'admin.users.column.username': 'प्रयोगकर्ता नाम',
  'admin.users.column.orgUnit': 'संगठन इकाई',
  'admin.users.column.roles': 'भूमिकाहरू',
  'admin.users.column.status': 'स्थिति',
  'admin.users.noRoles': 'हालसम्म कुनै भूमिका छैन',
  'admin.users.caption': 'कर्मचारी खाताहरू',
  'admin.users.empty.title': 'हालसम्म कुनै कर्मचारी खाता छैन',
  'admin.users.empty.body': 'माथिको "नयाँ प्रयोगकर्ता" बाट पहिलो कर्मचारी खाता सिर्जना गर्नुहोस्।',
  'admin.users.emptyFiltered.title': 'यो खोजसँग मिल्ने कुनै कर्मचारी छैन',
  'admin.users.emptyFiltered.body': 'फरक नाम, प्रयोगकर्ता नाम, वा स्थिति फिल्टर प्रयास गर्नुहोस्।',
  'admin.userCreate.title': 'नयाँ कर्मचारी खाता',
  'admin.userCreate.description': 'कर्मचारी खाता सिर्जना गर्नुहोस्। भूमिका छुट्टै रूपमा, खाताको आफ्नै पृष्ठबाट प्रदान गरिन्छ।',
  'admin.userCreate.username.label': 'प्रयोगकर्ता नाम',
  'admin.userCreate.username.hint': '३-६४ अक्षर: अक्षर, अंक, थोप्लो, अन्डरस्कोर वा हाइफन।',
  'admin.userCreate.username.error': 'मान्य प्रयोगकर्ता नाम प्रविष्ट गर्नुहोस् (३-६४ अक्षर)।',
  'admin.userCreate.fullName.label': 'पूरा नाम',
  'admin.userCreate.fullName.error': 'पूरा नाम आवश्यक छ।',
  'admin.userCreate.password.label': 'अस्थायी पासवर्ड',
  'admin.userCreate.password.hint': 'कम्तीमा {min} वर्ण। खाता धारकले पहिलो लगइनमा यो परिवर्तन गर्नुपर्छ।',
  'admin.userCreate.password.rejected.too_short': 'अस्थायी पासवर्ड कम्तीमा {min} वर्णको हुनुपर्छ।',
  'admin.userCreate.password.rejected.too_long': 'अस्थायी पासवर्ड बढीमा {max} वर्णको हुनुपर्छ।',
  'admin.userCreate.password.rejected.contains_username': 'अस्थायी पासवर्डमा प्रयोगकर्ता नाम हुनु हुँदैन।',
  'admin.userCreate.password.rejected.blocked_term': 'यसमा अनुमान गर्न सजिलो शब्द छ। अर्को छान्नुहोस्।',
  'admin.userCreate.password.error': 'अस्थायी पासवर्ड आवश्यक छ।',
  'admin.userCreate.email.label': 'इमेल (वैकल्पिक)',
  'admin.userCreate.phone.label': 'फोन (वैकल्पिक)',
  'admin.userCreate.locale.label': 'रोजाइको भाषा',
  'admin.userCreate.locale.ne': 'नेपाली',
  'admin.userCreate.locale.en': 'अंग्रेजी',
  'admin.userCreate.orgUnit.label': 'संगठन इकाई',
  'admin.userCreate.orgUnit.error': 'यो व्यक्ति सम्बन्धित इकाई छान्नुहोस्। इकाई नभएको खाताले फाइल राख्न सक्दैन र तपाईंको कर्मचारी सूचीमा देखिँदैन।',
  'admin.userCreate.submit': 'खाता सिर्जना गर्नुहोस्',
  'admin.userCreate.submitting': 'सिर्जना हुँदैछ',
  'admin.userCreate.toast.success': '{name} को खाता सिर्जना गरियो।',
  'admin.userCreate.error.duplicateUsername': 'यो प्रयोगकर्ता नाम यस मन्त्रालयमा पहिले नै प्रयोगमा छ।',
  'admin.orgUnitPicker.manualEntryHelp': 'तपाईंसँग यहाँ संगठन संरचना हेर्ने अनुमति छैन — संगठन इकाईको आईडी सिधै प्रविष्ट गर्नुहोस्।',
  'admin.orgUnitPicker.loadError': 'संगठन इकाईहरू लोड गर्न सकिएन। तपाईं अझै पनि संगठन इकाईको आईडी टाइप गर्न सक्नुहुन्छ।',
  'admin.userDetail.title': 'कर्मचारी खाता',
  'admin.userDetail.loading': 'कर्मचारी खाता लोड हुँदैछ',
  'admin.userDetail.backToDirectory': 'कर्मचारी निर्देशिकामा फर्कनुहोस्',
  'admin.userDetail.error.title': 'यो खाता लोड गर्न सकिएन',
  'admin.userDetail.error.body': 'यो खाता लोड गर्दा केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'admin.userDetail.profile.title': 'प्रोफाइल',
  'admin.userDetail.profile.edit': 'सम्पादन गर्नुहोस्',
  'admin.userDetail.profile.editTitle': 'प्रोफाइल सम्पादन गर्नुहोस्',
  'admin.userDetail.profile.email': 'इमेल',
  'admin.userDetail.profile.locale': 'रोजाइको भाषा',
  'admin.userDetail.profile.mfa': 'दुई-चरण प्रमाणीकरण',
  'admin.userDetail.profile.mfaOn': 'सक्षम',
  'admin.userDetail.profile.mfaOff': 'सेटअप गरिएको छैन',
  'admin.userDetail.profile.lastLogin': 'अन्तिम लगइन',
  'admin.userDetail.profile.never': 'कहिल्यै छैन',
  'admin.userDetail.profile.conflict': 'तपाईंले लोड गरेपछि यो खाता परिवर्तन भएको छ। पुनः लोड गरेर प्रयास गर्नुहोस्।',
  'admin.userDetail.toast.saved': 'प्रोफाइल अद्यावधिक भयो।',
  'admin.userDetail.status.title': 'खाता स्थिति',
  'admin.userDetail.deactivate.action': 'खाता निष्क्रिय गर्नुहोस्',
  'admin.userDetail.deactivate.selfNote': 'तपाईं आफ्नै खाता निष्क्रिय गर्न सक्नुहुन्न।',
  'admin.userDetail.roles.title': 'भूमिका असाइनमेन्टहरू',
  'admin.userDetail.roles.empty.title': 'हालसम्म कुनै भूमिका असाइनमेन्ट छैन',
  'admin.userDetail.roles.empty.body': 'भूमिका प्रदान नगरेसम्म यो खाताले केहीमा पहुँच पाउँदैन।',
  'admin.userDetail.roles.clearance': 'गोपनीयता तह {rank}',
  'admin.userDetail.roles.validFrom': '{date} देखि',
  'admin.userDetail.roles.validTo': '{date} सम्म',
  'admin.userDetail.sessions.title': 'सेसनहरू',
  'admin.userDetail.sessions.count': '{count} सक्रिय सेसन।',
  'admin.userDetail.sessions.manage': 'सेसनहरू व्यवस्थापन गर्नुहोस्',
  'admin.grant.action': 'भूमिका प्रदान गर्नुहोस्',
  'admin.grant.selfNote': 'तपाईं आफैंलाई भूमिका प्रदान गर्न सक्नुहुन्न — अर्को प्रशासकलाई सोध्नुहोस्।',
  'admin.grant.title': '{name} लाई भूमिका प्रदान गर्नुहोस्',
  'admin.grant.description': 'भूमिका, यसको दायरा, दायराले आवश्यक पर्दा संगठन-इकाई आधार, र गोपनीयता तह छनौट गर्नुहोस्।',
  'admin.grant.submit': 'प्रदान गर्नुहोस्',
  'admin.grant.submitting': 'प्रदान हुँदैछ',
  'admin.grant.role.label': 'भूमिका',
  'admin.grant.role.placeholder': 'भूमिका छनौट गर्नुहोस्',
  'admin.grant.role.error': 'भूमिका छनौट गर्नुहोस्।',
  'admin.grant.scope.label': 'दायरा',
  'admin.grant.scope.placeholder': 'दायरा छनौट गर्नुहोस्',
  'admin.grant.scope.error': 'दायरा छनौट गर्नुहोस्।',
  'admin.grant.scope.hint': 'यो भूमिकाले कति फराकिलो हेर्न र काम गर्न सक्छ।',
  'admin.grant.scope.platformOnlySuperAdmin': 'प्लेटफर्मभर दायरा सुपर एडमिन भूमिकालाई मात्र प्रदान गर्न सकिन्छ।',
  'admin.grant.anchor.label': 'संगठन-इकाई आधार',
  'admin.grant.anchor.hint': 'यो दायराका लागि आवश्यक — यो असाइनमेन्ट जोडिने संगठन संरचनाको स्थान।',
  'admin.grant.anchor.error': 'यो दायराका लागि संगठन इकाई आवश्यक छ।',
  'admin.grant.clearance.label': 'गोपनीयता तह',
  'admin.grant.clearance.placeholder': 'गोपनीयता तह छनौट गर्नुहोस्',
  'admin.grant.clearance.error': 'गोपनीयता तह छनौट गर्नुहोस्।',
  'admin.grant.error.catalogueLoad': 'भूमिका सूची लोड गर्न सकिएन। कृपया पुनः प्रयास गर्नुहोस्।',
  'admin.grant.error.noneGrantable': 'तपाईंसँग हाल कुनै भूमिका प्रदान गर्न दिने भूमिका छैन।',
  'admin.grant.error.userNotFound': 'यो खाता फेला पार्न सकिएन।',
  'admin.grant.toast.success': '{role} प्रदान गरियो।',
  'admin.grant.mfaTicket.title': 'एक-पटके प्रमाणीकरण सेटअप लिङ्क',
  'admin.grant.mfaTicket.body': 'यो भूमिकाका लागि दुई-चरण लगइन आवश्यक छ। नामांकन टोकन: {token} (म्याद {expiresAt})। यो खाता धारकलाई दिनुहोस् — यो फेरि देखाइने छैन।',
  'admin.revoke.action': 'खारेज गर्नुहोस्',
  'admin.revoke.title': 'यो भूमिका असाइनमेन्ट खारेज गर्ने हो?',
  'admin.revoke.description': 'यसले {name} बाट {role} ({scope}) हटाउँछ। यो उनको अर्को अनुरोधमा प्रभावकारी हुनेछ।',
  'admin.revoke.submit': 'खारेज गर्नुहोस्',
  'admin.revoke.submitting': 'खारेज हुँदैछ',
  'admin.revoke.toast.success': 'भूमिका असाइनमेन्ट खारेज गरियो।',
  'admin.revoke.error.alreadyEnded': 'त्यो असाइनमेन्ट पहिले नै समाप्त भइसकेको छ।',
  'admin.deactivate.title': '{name} को खाता निष्क्रिय गर्ने हो?',
  'admin.deactivate.description': 'उनलाई तुरुन्त लगआउट गरिनेछ र लगइन गर्न सक्नेछैनन्। यो यस स्क्रिनबाट पूर्ववत गर्न सकिँदैन।',
  'admin.deactivate.reason.label': 'कारण',
  'admin.deactivate.reason.error': 'कारण प्रविष्ट गर्नुहोस् (कम्तीमा ३ अक्षर)।',
  'admin.deactivate.submit': 'निष्क्रिय गर्नुहोस्',
  'admin.deactivate.submitting': 'निष्क्रिय हुँदैछ',
  'admin.deactivate.toast.success': '{name} को खाता निष्क्रिय गरियो।',
  'admin.deactivate.error.stillActive': 'खाता अझै सक्रिय छ — केही परिवर्तन भएन।',
  'admin.deactivate.error.selfOrConflict': 'यो खाता अहिले निष्क्रिय गर्न सकिँदैन — यो अझै सक्रिय छ।',
  'admin.deactivate.error.notFound': 'यो खाता फेला पार्न सकिएन। यो अझै सक्रिय छ।',
  'admin.sessions.title': 'सेसन व्यवस्थापन',
  'admin.sessions.breadcrumb': 'सेसनहरू',
  'admin.sessions.description': '{name} का सक्रिय सेसनहरू — डिभाइस, आईपी ठेगाना र अन्तिम गतिविधि।',
  'admin.sessions.caption': 'सक्रिय सेसनहरू',
  'admin.sessions.column.device': 'डिभाइस',
  'admin.sessions.column.ip': 'आईपी ठेगाना',
  'admin.sessions.column.lastSeen': 'अन्तिम सक्रिय',
  'admin.sessions.column.since': 'देखि लगइन गरिएको',
  'admin.sessions.column.actions': 'कार्यहरू',
  'admin.sessions.current': 'यो सेसन',
  'admin.sessions.unknownDevice': 'अज्ञात डिभाइस',
  'admin.sessions.empty.title': 'कुनै सक्रिय सेसन छैन',
  'admin.sessions.empty.body': 'यो खाता हाल कतै पनि लगइन गरिएको छैन।',
  'admin.sessions.revokeOne': 'साइन आउट गर्नुहोस्',
  'admin.sessions.revokeAll': 'सबैतिर साइन आउट गर्नुहोस्',
  'admin.sessions.revoking': 'साइन आउट हुँदैछ',
  'admin.sessions.revokeAllReason': 'सबै सेसनको प्रशासनिक साइन-आउट।',
  'admin.sessions.revokeOneConfirm.title': 'यो सेसन साइन आउट गर्ने हो?',
  'admin.sessions.revokeOneConfirm.description': 'यसले {name} का लागि एउटा डिभाइसको पहुँच तुरुन्त समाप्त गर्छ।',
  'admin.sessions.revokeAllConfirm.title': '{name} लाई सबैतिर साइन आउट गर्ने हो?',
  'admin.sessions.revokeAllConfirm.description': 'यसले {name} का सबै सक्रिय सेसन हरेक डिभाइसमा तुरुन्त समाप्त गर्छ।',
  'admin.sessions.toast.revokedOne': 'सेसन साइन आउट गरियो।',
  'admin.sessions.toast.revokedAll': '{count} सेसन साइन आउट गरियो।',
  'admin.sessions.error.stillActiveOne': 'त्यो सेसन साइन आउट गर्न सकिएन — यो अझै सक्रिय छ।',
  'admin.sessions.error.stillActiveAll': 'सबै सेसन साइन आउट गर्न सकिएन — केही अझै सक्रिय हुन सक्छन्।',
  'admin.org.title': 'संगठन संरचना',
  'admin.org.description': 'भूमिका दायरा र फाइल शाखाहरू निर्माण गरिने मन्त्रालय → महाशाखा → शाखा संरचना।',
  'admin.org.newMinistry': 'नयाँ शीर्ष-तह इकाई',
  'admin.org.includeArchived': 'सुरक्षित गरिएका इकाईहरू देखाउनुहोस्',
  'admin.org.empty.title': 'हालसम्म कुनै संगठन इकाई छैन',
  'admin.org.empty.body': 'यो वास्तविक पहिलो-चोटि अवस्था हो — माथि मन्त्रालयको पहिलो इकाई सिर्जना गर्नुहोस्।',
  'admin.org.error.title': 'संगठन संरचना लोड गर्न सकिएन',
  'admin.org.error.body': 'संगठन इकाईहरू लोड गर्दा केही समस्या भयो। कृपया पुनः प्रयास गर्नुहोस्।',
  'admin.org.addChild': '{kind} थप्नुहोस्',
  'admin.org.rename.action': 'नाम परिवर्तन गर्नुहोस्',
  'admin.org.archive.action': 'सुरक्षित गर्नुहोस्',
  'admin.org.toast.created': '{name} सिर्जना गरियो।',
  'admin.org.toast.renamed': '{name} अद्यावधिक गरियो।',
  'admin.org.toast.archived': '{name} सुरक्षित गरियो।',
  'admin.org.create.title': 'नयाँ {kind}',
  'admin.org.create.descriptionWithParent': 'यो {parent} अन्तर्गत सिर्जना हुनेछ।',
  'admin.org.create.descriptionRoot': 'यसले नयाँ शीर्ष-तह इकाई सिर्जना गर्छ।',
  'admin.org.create.submit': 'सिर्जना गर्नुहोस्',
  'admin.org.create.submitting': 'सिर्जना हुँदैछ',
  'admin.org.create.code.label': 'कोड',
  'admin.org.create.code.error': 'कोड आवश्यक छ।',
  'admin.org.create.nameNe.label': 'नाम (नेपाली)',
  'admin.org.create.nameNe.error': 'नेपाली नाम आवश्यक छ।',
  'admin.org.create.nameEn.label': 'नाम (अंग्रेजी)',
  'admin.org.create.nameEn.error': 'अंग्रेजी नाम आवश्यक छ।',
  'admin.org.create.error.duplicateCode': 'त्यो कोड यस मन्त्रालयमा पहिले नै प्रयोगमा छ।',
  'admin.org.create.error.badParent': 'त्यो अभिभावक इकाई यस प्रकारको इकाईका लागि मान्य छैन।',
  'admin.org.rename.title': '{name} नाम परिवर्तन गर्नुहोस्',
  'admin.org.rename.submit': 'सुरक्षित गर्नुहोस्',
  'admin.org.rename.submitting': 'सुरक्षित हुँदैछ',
  'admin.org.rename.error.conflict': 'तपाईंले लोड गरेपछि यो इकाई परिवर्तन भएको छ। पुनः लोड गरेर प्रयास गर्नुहोस्।',
  'admin.org.archive.title': '{name} सुरक्षित गर्ने हो?',
  'admin.org.archive.description': 'सुरक्षित गरिएका इकाईहरू छनौटकर्ताहरूबाट लुकाइन्छन् तर कहिल्यै मेटाइँदैनन्। यो इकाई खाली हुँदा मात्र गर्न सकिन्छ।',
  'admin.org.archive.submit': 'सुरक्षित गर्नुहोस्',
  'admin.org.archive.submitting': 'सुरक्षित हुँदैछ',
  'admin.org.archive.error.notFound': 'यो इकाई फेला पार्न सकिएन।',
  'admin.org.archive.error.notEmpty.title': 'यो इकाईमा अझै सक्रिय फाइल, कर्मचारी वा उप-इकाई छन्',
  'admin.org.archive.error.notEmpty.body': '{liveFiles} सक्रिय फाइल, {activeStaff} सक्रिय कर्मचारी र {liveChildren} उप-इकाई अझै जोडिएका छन्। सुरक्षित गर्नुअघि तिनीहरूलाई सार्नुहोस् वा समाधान गर्नुहोस्।',
  'admin.tenant.title': 'मन्त्रालयहरू',
  'admin.tenant.description': 'यस प्लेटफर्मका सबै मन्त्रालयहरू सुरु गर्नुहोस् र व्यवस्थापन गर्नुहोस्।',
  'admin.tenant.newTenant': 'मन्त्रालय सुरु गर्नुहोस्',
  'admin.tenant.caption': 'मन्त्रालयहरू',
  'admin.tenant.column.name': 'नाम',
  'admin.tenant.column.prefix': 'ट्र्याकिङ उपसर्ग',
  'admin.tenant.column.onboardedAt': 'सुरु गरिएको',
  'admin.tenant.column.status': 'स्थिति',
  'admin.tenant.column.actions': 'कार्यहरू',
  'admin.tenant.empty.title': 'हालसम्म यो मन्त्रालय मात्र अवस्थित छ',
  'admin.tenant.empty.body': 'केही बिग्रिएको छैन — यस प्लेटफर्ममा अहिलेसम्म अर्को कुनै मन्त्रालय सुरु गरिएको छैन।',
  'admin.tenant.status.active': 'सक्रिय',
  'admin.tenant.status.suspended': 'निलम्बित',
  'admin.tenant.edit.action': 'सम्पादन गर्नुहोस्',
  'admin.tenant.edit.title': '{name} सम्पादन गर्नुहोस्',
  'admin.tenant.edit.status.label': 'स्थिति',
  'admin.tenant.edit.suspendWarning': 'मन्त्रालय निलम्बन गर्दा त्यसका सबै कर्मचारी लगइन तुरुन्त रोकिन्छ।',
  'admin.tenant.toast.updated': '{name} अद्यावधिक गरियो।',
  'admin.tenant.toast.onboarded': '{name} सुरु गरियो।',
  'admin.tenant.create.title': 'नयाँ मन्त्रालय सुरु गर्नुहोस्',
  'admin.tenant.create.description': 'यसले भाडामा लिने संस्था, यसको ट्र्याकिङ-कोड उपसर्ग, यसको मूल संगठन इकाई र यसको पहिलो सचिव खाता सिर्जना गर्छ।',
  'admin.tenant.create.submit': 'सुरु गर्नुहोस्',
  'admin.tenant.create.submitting': 'सुरु हुँदैछ',
  'admin.tenant.create.required': 'यो फिल्ड आवश्यक छ।',
  'admin.tenant.create.prefix.label': 'ट्र्याकिङ-कोड उपसर्ग',
  'admin.tenant.create.prefix.error': '२-६ अक्षर मात्र प्रविष्ट गर्नुहोस्।',
  'admin.tenant.create.prefix.hint': 'पहिलो फाइल दर्ता भएपछि यो परिवर्तन गर्न सकिँदैन। ट्र्याकिङ कोडहरू {prefix}-2083-000001 जस्तो देखिनेछन्।',
  'admin.tenant.create.error.duplicatePrefix': 'त्यो उपसर्ग पहिले नै छुट्याइएको छ, सुरक्षित गरिएको मन्त्रालयलाई समेत।',
  'admin.tenant.create.nameNe.label': 'मन्त्रालयको नाम (नेपाली)',
  'admin.tenant.create.nameEn.label': 'मन्त्रालयको नाम (अंग्रेजी)',
  'admin.tenant.create.rootOrgUnitCode.label': 'मूल संगठन-इकाई कोड',
  'admin.tenant.create.rootOrgUnitCode.hint': 'जस्तै HQ — यस मन्त्रालयको आफ्नै संगठन संरचनाको शीर्ष।',
  'admin.tenant.create.secretary.heading': 'पहिलो सचिव खाता',
  'admin.tenant.create.secretary.username.label': 'प्रयोगकर्ता नाम',
  'admin.tenant.create.secretary.fullName.label': 'पूरा नाम',
  'admin.tenant.create.secretary.password.label': 'अस्थायी पासवर्ड',
  'admin.tenant.create.mfaTicket.body': 'सचिव खाताहरूलाई दुई-चरण लगइन आवश्यक पर्छ। {username} का लागि नामांकन टोकन: {token} (म्याद {expiresAt})। यो छुट्टै माध्यमबाट उनलाई दिनुहोस् — यो फेरि देखाइने छैन।',
  'admin.audit.title': 'प्लेटफर्म लेखा परीक्षण लग',
  'admin.audit.description': 'यस प्लेटफर्मका सबै क्रस-टेनेन्ट प्रशासनिक कार्यहरू। पढ्नमात्र — यहाँ केही पनि सम्पादन वा मेटाउन सकिँदैन।',
  'admin.audit.caption': 'प्लेटफर्म लेखा परीक्षण घटनाहरू',
  'admin.audit.column.occurredAt': 'कहिले',
  'admin.audit.column.category': 'श्रेणी',
  'admin.audit.column.action': 'कार्य',
  'admin.audit.column.outcome': 'नतिजा',
  'admin.audit.column.subject': 'विषय',
  'admin.audit.column.crossTenant': 'क्रस-टेनेन्ट',
  'admin.audit.crossTenantYes': 'हो',
  'admin.audit.crossTenantNo': 'होइन',
  'admin.audit.filter.category': 'श्रेणी',
  'admin.audit.filter.action': 'कार्य',
  'admin.audit.filter.from': 'देखि',
  'admin.audit.filter.to': 'सम्म',
  'admin.audit.filter.limit': 'पङ्क्तिहरू',
  'admin.audit.filter.apply': 'फिल्टर लागू गर्नुहोस्',
  'admin.audit.truncated': 'नयाँतम {limit} मिल्दो घटनाहरू देखाइँदैछ। थप विशिष्ट नतिजाका लागि माथिको फिल्टर साँघुरो बनाउनुहोस्।',
  'admin.audit.empty.title': 'कुनै मिल्दो लेखा परीक्षण घटना छैन',
  'admin.audit.empty.body': 'यी फिल्टरसँग कुनै प्लेटफर्म-स्तरको प्रशासनिक कार्य मेल खाँदैन।',
};

const DICTIONARIES: Record<Locale, Dict> = { en, ne };

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
}

export function translate(locale: Locale, key: string, vars?: Record<string, string | number>): string {
  const dictionary = DICTIONARIES[locale];
  const template = dictionary[key] ?? DICTIONARIES.en[key] ?? key;
  return interpolate(template, vars);
}

/** `useT()` returns a function bound to the app's currently active locale. */
export function useT(): (key: string, vars?: Record<string, string | number>) => string {
  const { locale } = useLocale();
  return (key: string, vars?: Record<string, string | number>) => translate(locale, key, vars);
}
